import { Fragment, type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import type {
  ConversionRow,
  ConversionsPayload,
  GoogleCampaignsPayload,
  MetaAdSet,
  MetaCampaignsPayload,
  RedditCampaignsPayload,
  MgidCampaignsPayload,
  TaboolaCampaignsPayload,
  MsAdsCampaign,
  MsAdsAdGroup,
  OpenaiAccount,
  OpenaiCampaign,
  OpenaiCampaignsPayload,
  OpenaiTrackerCount,
  KampagnenPayload,
  OnexbetPayload,
  RoboforexKampagnenPayload,
} from "@dashboard/shared";
import { Apple, BarChart3, ChevronDown, ChevronUp, DollarSign, Euro, Info, Monitor, MousePointer2, Smartphone, X } from "lucide-react";
import { api } from "@/lib/api";
import { ACCOUNT_LABEL, accountLabel, accountMarke, accountRank } from "@/lib/accounts";
import { toUsd, useEurUsd } from "@/lib/fx";
import {
  PRODUCTS, brokerPasst, productHidden, productLabel, productRank, stageShort, vergleichKonto,
} from "@/lib/products";
import { ProductIcon, SourceIcon, sourceTint } from "@/components/icons";
import { KapitalKarte } from "./KapitalKarte";
import { SeiteKarte } from "./SeiteKarte";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ZeitraumWaehler } from "@/components/ZeitraumWaehler";
import {
  defaultDateRange,
  toQueryParams,
  type DateRangeValue,
} from "@/components/DateRangePicker";
import { cn, fmtInt, fmtMoney, fmtUsd, fmtAmount } from "@/lib/utils";

type Totals = { spend: number; clicks: number; conversions: number; costPerConv: number | null };

/** Summe der Impressionen (fehlt das Feld, zaehlt es als 0). */
const impr = (list: { impressions?: number }[]) => list.reduce((s, c) => s + (c.impressions ?? 0), 0);

function aggregate(list: { spend: number; clicks: number; conversions: number }[]): Totals {
  const spend = list.reduce((s, c) => s + c.spend, 0);
  const clicks = list.reduce((s, c) => s + c.clicks, 0);
  const conversions = list.reduce((s, c) => s + c.conversions, 0);
  return { spend, clicks, conversions, costPerConv: conversions > 0 ? spend / conversions : null };
}

// Drei verschiedene Zustaende, die frueher alle als "?" erschienen:
//   microsoft-noid  Anzeigenklick erkannt (UTM-Auto-Tagging), aber die Klick-ID fehlte
//                   schon in der Adresse — vom Browser des Nutzers entfernt. Kein Upload
//                   moeglich, Kampagne trotzdem bekannt.
//   other           Klick erfasst, keinerlei Werbe-Kennung (direkt/organisch/Test)
//   unknown         zur Conversion existiert gar keine Klickzeile
const SOURCE_LABEL: Record<string, string> = {
  google: "Google", microsoft: "Microsoft", applovin: "AppLovin", openai: "OpenAI",
  "microsoft-noid": "MS ohne ID", "google-noid": "Google ohne ID", meta: "Meta", "meta-noid": "Meta ohne ID", reddit: "Reddit", mgid: "MGID", taboola: "Taboola",
  other: "?", unknown: "kein Klick",
};
const geoLabel = (geo: string) => (geo === "unknown" ? "?" : geo.toUpperCase());

/** Conversions nach Werbequelle gruppiert: je Quelle eine Zeile mit ihrer Summe,
 *  dahinter die Laender. Vorher stand jede Land-Quelle-Kombination einzeln
 *  nebeneinander ("ES · ChatGPT", "FR · Microsoft", "DE · ChatGPT"), sodass sich
 *  die Quellen nicht vergleichen liessen, ohne die Chips im Kopf zu sortieren.
 *  Nimmt jede Zeilenform mit geo/source/count — die Tracker-Conversions aller Produkte. */
function StageBadges({ rows, plain }: { rows: { geo: string; source: string; count: number }[]; plain?: boolean }) {
  if (rows.length === 0) return null;

  const bySource = new Map<string, { total: number; geos: Map<string, number> }>();
  for (const r of rows) {
    const entry = bySource.get(r.source) ?? { total: 0, geos: new Map<string, number>() };
    entry.total += r.count;
    entry.geos.set(r.geo, (entry.geos.get(r.geo) ?? 0) + r.count);
    bySource.set(r.source, entry);
  }
  const groups = [...bySource.entries()]
    .map(([source, v]) => ({
      source,
      total: v.total,
      geos: [...v.geos.entries()]
        .map(([geo, count]) => ({ geo, count }))
        .sort((a, b) => b.count - a.count || a.geo.localeCompare(b.geo)),
    }))
    .sort((a, b) => b.total - a.total || a.source.localeCompare(b.source));

  return (
    <div className={cn("flex flex-col gap-2", plain ? "" : "mt-3 border-t border-border pt-2.5")}>
      {groups.map((g) => (
        <div key={g.source} className="flex flex-wrap items-center gap-1.5 border-t border-border/60 pt-2 first:border-t-0 first:pt-0">
          <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded ${sourceTint(g.source)}`}>
            <SourceIcon source={g.source} className="h-3 w-3" />
          </span>
          <span className="text-[13px] font-semibold">
            {SOURCE_LABEL[g.source] ?? g.source}
            <span className="ml-1.5 tabular-nums font-normal text-muted-foreground">{g.total}</span>
          </span>
          <span className="flex w-full flex-wrap items-center gap-1">
            {g.geos.map((x) => (
              <span
                key={x.geo}
                className="inline-flex items-center gap-1 rounded border border-primary/25 bg-primary/5 px-1.5 py-0.5 text-[11px] font-medium text-primary"
              >
                {geoLabel(x.geo)}
                <span className="tabular-nums opacity-70">{x.count}</span>
              </span>
            ))}
          </span>
        </div>
      ))}
    </div>
  );
}

/** Geraete der Conversions (Operator 2026-09-25): Anteilsbalken plus Chips mit Symbol. Seit 2026-10-09 kurz und
 *  etwas hoeher (Operator).
 *  Neutral gehalten, damit sie sich von den blauen Laender-Chips abheben. Das Geraet kommt aus
 *  der Browser-Kennung des Klicks (Tracker); ohne Klick steht "?". */
const GERAETE = [
  { key: "desktop", label: "Desktop", Icon: Monitor, balken: "bg-primary" },
  { key: "ios", label: "iPhone", Icon: Apple, balken: "bg-primary/55" },
  { key: "android", label: "Android", Icon: Smartphone, balken: "bg-primary/25" },
  { key: "unknown", label: "?", Icon: null, balken: "bg-muted-foreground/30" },
] as const;

function DeviceSplit({ rows }: { rows: { device?: string; count: number }[] }) {
  const n = new Map<string, number>();
  for (const r of rows) {
    const d = GERAETE.some((g) => g.key === r.device) ? (r.device as string) : "unknown";
    n.set(d, (n.get(d) ?? 0) + r.count);
  }
  const gesamt = [...n.values()].reduce((a, b) => a + b, 0);
  if (gesamt === 0) return null;
  const teile = GERAETE.filter((g) => (n.get(g.key) ?? 0) > 0);
  // Seit 2026-10-09 (Operator) in EINER Zeile direkt neben der Conversion-Zahl: kurzer Balken, daneben die Chips.
  return (
    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1" data-geraete>
      <div className="flex h-2.5 w-12 min-w-[1.25rem] shrink overflow-hidden rounded-full bg-muted" aria-hidden="true">
        {teile.map((g) => (
          <div key={g.key} className={g.balken} style={{ width: `${((n.get(g.key) ?? 0) / gesamt) * 100}%` }} />
        ))}
      </div>
      {teile.map((g) => {
        const k = n.get(g.key) ?? 0;
        return (
          <span
            key={g.key}
            className="inline-flex items-center gap-0.5 rounded border border-border bg-muted/60 px-1 py-0.5 text-[11px] font-medium text-foreground/80"
            title={`${g.label}: ${k} (${Math.round((k / gesamt) * 100)} %)`} aria-label={`${g.label} ${k}`}
          >
            {/* Nur Symbol + Zahl (Name im Tooltip), damit Balken und Chips neben die Zahl passen. */}
            {g.Icon ? <g.Icon className="h-3 w-3 opacity-70" /> : g.label}
            <span className="tabular-nums opacity-70">{k}</span>
          </span>
        );
      })}
    </div>
  );
}

// ---- Conversions je Marke im eToro-Stil: drei Spalten Sign-ups / FTDs / Trader ----
// (Operator 2026-09-29: eToro pausiert und ausgeblendet. 2026-09-30: BlackBull raus, dafuer je Produkt eine Spalte.)

/** Die Marken, links nach rechts: alle sichtbaren Produkte mit `spalte` aus der Registry. Seit 2026-10-01 zwei Ebenen
 *  (Operator): oben die Haupt-Einnahmequellen (eToro ueber Microsoft, AvaTrade ueber ChatGPT) in halber Breite,
 *  darunter die Tests kompakt in einer Reihe, ganz unten die Werbekonten in derselben Anordnung. */
const SPALTEN = PRODUCTS.filter((p) => p.spalte && !p.hidden).sort((a, b) => a.rank - b.rank);
const HAUPT: readonly string[] = SPALTEN.filter((p) => p.haupt).map((p) => p.slug);
const TESTS: readonly string[] = SPALTEN.filter((p) => !p.haupt).map((p) => p.slug);
const MARKEN: readonly string[] = [...HAUPT, ...TESTS];
/** Tests in zwei Reihen (Operator 2026-10-09): oben 1xBet und eToro · Google, darunter RoboForex, Deel, OpenAI · Top 10
 *  und Pepperstone. Reihe aus der Registry (`reihe`), Reihenfolge = rank. */
const TEST_REIHEN: readonly (readonly string[])[] = [2, 3]
  .map((r) => SPALTEN.filter((p) => !p.haupt && (p.reihe ?? 2) === r).map((p) => p.slug))
  .filter((r) => r.length > 0);
const markeTitel = (slug: string) => PRODUCTS.find((p) => p.slug === slug)?.marke ?? productLabel(slug);
/** Gehoeren die OpenAI-Konten der Marke in ihre Karte (Kacheln + Kostenbasis)? Nicht bei Karten, die nur bestimmte
 *  Quellen zaehlen und OpenAI nicht dazugehoert (eToro · Microsoft seit 2026-10-02) - sonst stuenden ChatGPT-Kosten
 *  gegen Microsoft-Registrierungen. */
const zaehltOpenai = (slug: string) => {
  const p = PRODUCTS.find((x) => x.slug === slug);
  return !p?.quellen || p.quellen.includes("openai");
};
/** Broker-Praefixe der Werbekonten einer Marke: der Slug selbst, bei der Vergleichsseite die Praefixe ihrer Zielmarken
 *  (etorovgl, avatradevgl). Zuordnung immer ueber `brokerPasst`, damit etorovgl1 nicht bei eToro landet. */
const kontoPraefixe = (slug: string): string[] =>
  PRODUCTS.find((x) => x.slug === slug)?.vergleich?.map((v) => v.praefix) ?? [slug];
/** Marke eines OpenAI-Werbekontos (fuer die Kostenbasis). */
const markeDesKontos = (broker: string) => MARKEN.find((m) => kontoPraefixe(m).some((pf) => brokerPasst(broker, pf)));

type MarkenCell = {
  /** label = Kurzform (Spaltenkopf), titel = Langform (Tooltip); subTitel erklaert die Kostenzeile.
   *  labelGross = Beschriftung in den grossen Karten der Haupt-Einnahmequellen ("Sign-ups" statt "Reg"). */
  key: string; label: string; labelGross: string; titel: string; display: string; sub: string | null; subTitel?: string; badges: ConversionRow[];
  /** Nur die Trader-Spalte: Summe der CPA-Werte und Verhaeltnis zu den Ausgaben der Marke. */
  wert?: { betrag: string; faktor: string | null; titel?: string } | null;
  /** Vorstufe als Zusatzzeile (BlackBull-Leads), zaehlt nicht in `display`. */
  vorstufe?: { label: string; anzahl: string; kosten: string | null };
};

const WERT_TRADER_ERKLAERUNG =
  "Summe der Werte laut Tracker: AvaTrade 750 $ je gemeldetem First Trade (von AvaPartner noch nicht als " +
  "CPA bestaetigt), Deel 500 $ je qualifizierter Chance und 1.000 $ je Abschluss, NordVPN und HighLevel die " +
  "gemeldete Provision. Die Zahl in Klammern ist der Wert geteilt durch die " +
  "Ausgaben der Marke im Zeitraum. OpenAI bucht Ausgaben mit Verzoegerung.";

/** Eine Marke als EINE Karte (Operator 2026-10-01): oben die Conversions, unten als abgesetzte Fussleiste die
 *  Werbekonten der Marke. Die Fussleiste sitzt immer am Kartenboden, damit sie bei gleich hohen Karten einer Reihe
 *  auf derselben Hoehe steht. */
function MarkenConversions({ slug, titel, cells, gross, konten }: {
  slug: string; titel: string; cells: MarkenCell[]; gross?: boolean; konten?: ReactNode;
}) {
  // Haupt-Einnahmequellen: Sign-ups bekommt die halbe Breite - dort stehen die meisten Chips (Operator 2026-10-01).
  const zweizeilig = Boolean(gross && cells.some((c) => c.wert));
  const spalten = gross && cells.length > 1
    ? `minmax(0, 2fr) repeat(${cells.length - 1}, minmax(0, 1fr))`
    : `repeat(${Math.max(cells.length, 1)}, minmax(0, 1fr))`;
  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border border-border bg-card/60" data-conv={slug}>
      <div className={cn("flex-1", gross ? "p-3.5" : "p-2.5")}>
        {/* Kompakt fuer fuenf Test-Spalten in der normalen Seitenbreite (Operator 2026-09-30); die zwei Haupt-
            Einnahmequellen (gross) haben die halbe Breite und damit Platz fuer die Chips direkt unter jeder Stufe. */}
        <div className={cn("flex items-center", gross ? "gap-2" : "gap-1.5")}>
          <ProductIcon slug={slug} className={gross ? "h-6 w-6" : "h-5 w-5"} />
          <h3 className={cn("truncate font-semibold", gross ? "text-base" : "text-[13px]")} title={titel}>{titel}</h3>
        </div>
        {/* So viele Zellen wie Stufen (NordVPN 1, ClickBank 2, sonst 3) - volle Kartenbreite, nichts wird abgeschnitten. */}
        <div className={cn("grid divide-x divide-border", gross ? "mt-3" : "mt-2")} style={{ gridTemplateColumns: spalten }}>
          {cells.map((c, i) => (
            <div key={c.key} className={cn("min-w-0", i > 0 && (gross ? "pl-3" : "pl-1.5"), i < cells.length - 1 && (gross ? "pr-3" : "pr-1.5"))} data-conv={`${slug}:${c.key}`}>
              <div className={cn("whitespace-nowrap font-semibold text-muted-foreground", gross ? "text-xs" : "text-[10px]")} title={c.titel}>{gross ? c.labelGross : c.label}</div>
              {/* Zahl und Geraete (Balken + Chips) in einer Zeile - keine eigene Reihe dafuer (Operator 2026-10-09). */}
              <div className="mt-1 flex items-center gap-2">
                <div className={cn("shrink-0 font-bold leading-none tabular-nums", gross ? "text-[1.75rem]" : "text-xl")}>{c.display}</div>
                {c.badges.length > 0 && <DeviceSplit rows={c.badges} />}
              </div>
              <div className={cn("mt-1 min-h-[1rem] whitespace-nowrap tabular-nums text-muted-foreground", gross ? "text-xs" : "text-[11px]")} title={c.subTitel}>{c.sub ?? ""}</div>
              {/* Zeile bleibt in allen Spalten reserviert, damit Balken und Chips auf einer Hoehe stehen. In den grossen
                  Karten ist die Trader-Spalte nur ein Viertel breit: der Wert bricht dort um, also zwei Zeilen reservieren. */}
              <div className={cn("text-[11px] text-muted-foreground", zweizeilig ? "min-h-[2rem]" : "min-h-[1rem]")} data-wert={c.wert ? "" : undefined}
                data-vorstufe={c.vorstufe ? "" : undefined}>
                {c.vorstufe && (
                  <span className="whitespace-nowrap">
                    {c.vorstufe.label} <span className="font-semibold tabular-nums text-foreground">{c.vorstufe.anzahl}</span>
                    {c.vorstufe.kosten && <span className="tabular-nums"> · {c.vorstufe.kosten}</span>}
                  </span>
                )}
                {c.wert && (
                  <span className="inline-flex cursor-help flex-wrap items-center gap-x-1" title={c.wert.titel ?? WERT_TRADER_ERKLAERUNG}>
                    <span className="whitespace-nowrap">Wert ≈ <span className="font-semibold tabular-nums text-foreground">{c.wert.betrag}</span></span>
                    <span className="inline-flex items-center gap-1 whitespace-nowrap">
                      {c.wert.faktor && <span className="tabular-nums">({c.wert.faktor})</span>}
                      <Info className="h-3.5 w-3.5 shrink-0 opacity-60" />
                    </span>
                  </span>
                )}
              </div>
              {/* Grosse Karte: Quellen- und Laender-Chips direkt unter der eigenen Stufe (Geraete stehen neben der Zahl). */}
              {gross && c.badges.length > 0 && (
                <div className="mt-2.5 border-t border-border pt-2.5" data-conv-badges={`${slug}:${c.key}`}>
                  <StageBadges rows={c.badges} plain />
                </div>
              )}
            </div>
          ))}
        </div>
        {/* Kompakte Karte: Quellen- und Laender-Chips unter den Zahlen in voller Kartenbreite - in einer Drittel-Zelle
            einer Fuenftel-Spalte stapelten sie sich sonst zu einer langen Liste. */}
        {!gross && cells.filter((c) => c.badges.length > 0).map((c) => (
          <div key={c.key} className="mt-2 border-t border-border pt-2" data-conv-badges={`${slug}:${c.key}`}>
            {cells.filter((x) => x.badges.length > 0).length > 1 && (
              <div className="mb-1 text-[11px] font-semibold text-muted-foreground">{c.label}</div>
            )}
            <StageBadges rows={c.badges} plain />
          </div>
        ))}
      </div>
      {konten && (
        <div className={cn("border-t border-border bg-muted/40", gross ? "px-3.5 py-3" : "px-2.5 py-2.5")} data-section={`Werbekonten ${slug}`}>
          {konten}
        </div>
      )}
    </div>
  );
}

/** Eine Zahl der Vergleichskarte: Stufe einer Zielmarke. */
type VergleichZelle = {
  key: string; label: string; titel: string; count: number; value: number; display: string; badges: ConversionRow[];
};
type VergleichDaten = {
  /** Je Zielmarke (eToro, AvaTrade) eine Zeile mit allen Stufen. */
  zeilen: { marke: string; label: string; cells: VergleichZelle[] }[];
  /** Kosten je Stufe ueber beide Zielmarken (die Ausgaben gehoeren zu beiden). */
  kosten: { key: string; sub: string; titel?: string }[];
  wert: { betrag: string; faktor: string | null; titel: string } | null;
};

/** Herkunft einer Zahl als Tooltip-Text: je Quelle die Laender, darunter die Geraete ("OpenAI 4: FR 2 · DE 1"). */
function herkunftText(rows: ConversionRow[]): string {
  const quellen = new Map<string, Map<string, number>>();
  const geraete = new Map<string, number>();
  for (const r of rows) {
    const q = quellen.get(r.source) ?? new Map<string, number>();
    q.set(r.geo, (q.get(r.geo) ?? 0) + r.count);
    quellen.set(r.source, q);
    const g = GERAETE.find((x) => x.key === r.device)?.label ?? "?";
    geraete.set(g, (geraete.get(g) ?? 0) + r.count);
  }
  const summe = (m: Map<string, number>) => [...m.values()].reduce((a, b) => a + b, 0);
  const liste = (m: Map<string, number>, name: (k: string) => string) =>
    [...m.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([k, n]) => `${name(k)} ${n}`).join(" · ");
  return [
    ...[...quellen.entries()].sort((a, b) => summe(b[1]) - summe(a[1]))
      .map(([q, m]) => `${SOURCE_LABEL[q] ?? q} ${summe(m)}: ${liste(m, geoLabel)}`),
    GERAETE.filter((g) => geraete.has(g.label)).map((g) => `${g.label} ${geraete.get(g.label)}`).join(" · "),
  ].join("\n");
}

/** Karte "OpenAI · Top 10 Vergleich" (Operator 2026-10-06): Aufbau und Masse wie die kompakten Karten, aber je
 *  Zielmarke eine Zahlenzeile (Logo links), darunter die Kosten je Stufe beider Marken zusammen. Laender und Geraete
 *  stehen im Tooltip der Zahl - als Chips waeren es bis zu sechs Bloecke, die die ganze Reihe in die Hoehe ziehen. */
function VergleichConversions({ slug, titel, daten, konten }: {
  slug: string; titel: string; daten?: VergleichDaten; konten?: ReactNode;
}) {
  const zeilen = daten?.zeilen ?? [];
  const n = Math.max(zeilen[0]?.cells.length ?? 0, 1);
  // Erste Spalte: Logo der Zielmarke; dann je Stufe eine Spalte mit Trennlinie wie in den anderen Karten.
  const spalten = `1.375rem repeat(${n}, minmax(0, 1fr))`;
  const zelle = (i: number) => cn("min-w-0", i > 0 && "border-l border-border pl-1.5", i < n - 1 && "pr-1.5");
  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border border-border bg-card/60" data-conv={slug}>
      <div className="flex-1 p-2.5">
        <div className="flex items-center gap-1.5">
          <ProductIcon slug={slug} className="h-5 w-5 shrink-0" />
          {/* Langer Name: bricht in schmalen Karten (1280 px) um, statt mit "…" abgeschnitten zu werden. */}
          <h3 className="min-w-0 text-[13px] font-semibold leading-tight" title={titel}>{titel}</h3>
        </div>
        <div className="mt-2 grid" style={{ gridTemplateColumns: spalten }}>
          <div />
          {(zeilen[0]?.cells ?? []).map((c, i) => (
            <div key={c.key} className={cn(zelle(i), "whitespace-nowrap text-[10px] font-semibold text-muted-foreground")}
              title={c.titel.split(" · ").pop()}>{c.label}</div>
          ))}
          {zeilen.map((z) => (
            <Fragment key={z.marke}>
              <div className="flex items-center pt-1.5" title={z.label} data-vergleich-marke={z.marke}>
                <ProductIcon slug={z.marke} className="h-4 w-4" />
              </div>
              {z.cells.map((c, i) => (
                <div key={c.key} className={cn(zelle(i), "pt-1.5 text-xl font-bold leading-none tabular-nums")}
                  data-conv={`${slug}:${z.marke}:${c.key}`}
                  title={`${c.titel}: ${c.display}${c.value > 0 ? ` · Wert ${fmtUsd(c.value)}` : ""}${c.badges.length ? `\n${herkunftText(c.badges)}` : ""}`}>
                  {c.display}
                </div>
              ))}
            </Fragment>
          ))}
          <div />
          {(daten?.kosten ?? []).map((k, i) => (
            <div key={k.key} className={cn(zelle(i), "min-h-[1rem] whitespace-nowrap pt-1 text-[11px] tabular-nums text-muted-foreground")}
              title={k.titel}>{k.sub}</div>
          ))}
        </div>
        {daten?.wert && (
          <div className="mt-1 text-[11px] text-muted-foreground" data-wert="">
            <span className="inline-flex cursor-help flex-wrap items-center gap-x-1" title={daten.wert.titel}>
              <span className="whitespace-nowrap">Wert ≈ <span className="font-semibold tabular-nums text-foreground">{daten.wert.betrag}</span></span>
              <span className="inline-flex items-center gap-1 whitespace-nowrap">
                {daten.wert.faktor && <span className="tabular-nums">({daten.wert.faktor})</span>}
                <Info className="h-3.5 w-3.5 shrink-0 opacity-60" />
              </span>
            </span>
          </div>
        )}
      </div>
      {konten && (
        <div className="border-t border-border bg-muted/40 px-2.5 py-2.5" data-section={`Werbekonten ${slug}`}>
          {konten}
        </div>
      )}
    </div>
  );
}

const WERT_VERGLEICH_ERKLAERUNG =
  "Summe der Trader-Werte laut Tracker: eToro mit dem Betrag aus dem Postback (je Land), AvaTrade 750 $ je " +
  "gemeldetem First Trade. Die Zahl in Klammern ist der Wert geteilt durch die Ausgaben beider Werbekonten im " +
  "Zeitraum. OpenAI bucht Ausgaben mit Verzoegerung.";

// Kompakt genug für zwei Panels nebeneinander (7 Spalten in ~440px).
// Zahlenspalten nehmen ihre Mindestbreite, die Namensspalte (w-full + max-w-0) den Rest und
// kürzt mit "…" — so wächst die Tabelle nie über das Panel hinaus.
const TD = "px-1 py-1.5 text-right text-[13px] tabular-nums";
const TH = "px-1 py-1.5 text-right font-medium";
const TD_NAME = "w-full max-w-0 truncate px-1.5 py-1.5 text-left text-[13px] font-medium";
const TD_SUM = "px-1.5 py-1.5 text-left text-[13px]";

function openaiActive(c: OpenaiCampaign): boolean {
  return c.status === "active";
}

/** Hat die Kampagne im Zeitraum ueberhaupt etwas getan? */
function openaiHatZahlen(c: OpenaiCampaign): boolean {
  return c.spend > 0 || c.impressions > 0 || c.clicks > 0;
}

/** Zeilen der Detailtabelle: alles, was laeuft ODER im Zeitraum Geld gekostet hat.
 *  Pausierte und archivierte Kampagnen haben vorher im Zeitraum ausgegeben - ohne sie
 *  waere die Summe kleiner als die tatsaechlichen Kosten (Operator 2026-09-18). */
function openaiZeile(c: OpenaiCampaign): boolean {
  return openaiActive(c) || openaiHatZahlen(c);
}

const STATUS_KURZ: Record<string, string> = { paused: "pausiert", archived: "archiviert" };

/** Tracker-Conversions (Klickquelle ChatGPT) eines Brokers als Kurzform: "3 Reg · 1 FTD". Werbekonten der
 *  Vergleichsseite: eToro und AvaTrade desselben Kontos ("2 eToro Reg · 1 AvaTrade Reg"). */
function trackerNote(broker: string, rows: OpenaiTrackerCount[]): string {
  const konto = vergleichKonto(broker);
  return rows
    .filter((r) => r.broker === broker || (konto !== null && vergleichKonto(r.broker) === konto))
    .map((r) => `${fmtInt(r.count)} ${konto !== null ? `${r.broker.startsWith("avatrade") ? "AvaTrade" : "eToro"} ` : ""}${stageShort(broker, r.stage)}`)
    .join(" · ");
}

/** Aktive Kampagnen EINES OpenAI-Kontos (= eines Produkts) als flache Liste. */
function OpenaiCampaignTable({
  account, tracker, errors,
}: {
  account: OpenaiAccount; tracker: OpenaiTrackerCount[]; errors?: string[];
}) {
  const rows = account.campaigns.filter(openaiZeile)
    .sort((x, y) => y.spend - x.spend || y.clicks - x.clicks);
  const currency = account.currency;
  const agg = aggregate(rows);
  const note = trackerNote(account.broker, tracker);
  return (
    <>
      <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-border text-xs text-muted-foreground">
            <th className="px-1.5 py-1.5 text-left font-medium">Kampagne</th>
            <th className={TH}>Impr.</th>
            <th className={TH}>Klicks</th>
            <th className={TH}>Ausgaben</th>
            <th className={TH}>Conv.</th>
            <th className={TH}>CPA</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.campaignId} className="border-t border-border/50">
              <td className={TD_NAME} title={c.campaignName}>
                {c.campaignName}
                {!openaiActive(c) && (
                  <span className="ml-1.5 text-[11px] font-normal text-muted-foreground">
                    {STATUS_KURZ[c.status] ?? c.status}
                  </span>
                )}
              </td>
              <td className={TD}>{fmtInt(c.impressions)}</td>
              <td className={TD}>{fmtInt(c.clicks)}</td>
              <td className={`${TD} font-medium`}>{fmtMoney(c.spend, currency)}</td>
              <td className={TD}>{fmtInt(c.conversions)}</td>
              <td className={TD}>{c.costPerConversion != null ? fmtMoney(c.costPerConversion, currency) : "—"}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={6} className="py-4 text-center text-sm text-muted-foreground">Keine Kampagnen mit Daten im Zeitraum.</td></tr>
          )}
        </tbody>
        {rows.length > 0 && (
          <tfoot className="border-t border-border font-medium">
            <tr>
              <td className={TD_SUM}>Summe</td>
              <td className={TD}>{fmtInt(rows.reduce((s, c) => s + c.impressions, 0))}</td>
              <td className={TD}>{fmtInt(agg.clicks)}</td>
              <td className={TD}>{fmtMoney(agg.spend, currency)}</td>
              <td className={TD}>{fmtInt(agg.conversions)}</td>
              <td className={TD}>{agg.costPerConv != null ? fmtMoney(agg.costPerConv, currency) : "—"}</td>
            </tr>
          </tfoot>
        )}
      </table>
      </div>
      <div className="mt-1 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <span title="Conversions laut eigenem Tracker (Klickquelle OpenAI Ads), unabhängig von OpenAIs verzögertem Reporting">
          {note ? `Tracker: ${note}` : "Tracker: keine Conversions im Zeitraum"}
        </span>
        <span>Conv. = von OpenAI attribuiert</span>
      </div>
      {errors?.length ? (
        <div className="mt-2 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">{errors.join(" · ")}</div>
      ) : null}
    </>
  );
}

// ---- Microsoft (seit 2026-10-01 wieder aktiv: $2-$5, $1 wird von Flo verwaltet und nur angezeigt) ----

function msActive(c: MsAdsCampaign): boolean {
  const s = (c.status ?? "").toLowerCase();
  return s.includes("active") || s === "budgetpaused";
}

/** Kampagnen mit FTD als Microsoft-Ziel ("eToro | FTD | ...", seit 2026-10-15): Microsofts "Conversions" zaehlt dort nur FTDs.
 *  Im Dashboard zaehlen auch dort die Registrierungen (Sign-up-Ziel, MS-Attribution) als Conv. - die FTDs stehen ohnehin in
 *  der FTD-Spalte (Operator 2026-10-19). */
function msMitRegs(c: MsAdsCampaign): MsAdsCampaign {
  if (!/\|\s*FTD\s*\|/i.test(c.campaignName) || c.goalSignups == null) return c;
  const conversions = c.goalSignups;
  return { ...c, conversions, costPerConversion: conversions > 0 ? c.spend / conversions : null };
}

/** Wie bei OpenAI: was laeuft ODER im Zeitraum etwas gekostet hat - sonst fehlten die Kosten einer
 *  Kampagne, die im Zeitraum pausiert wurde (Operator 2026-09-18). */
function msZeile(c: MsAdsCampaign): boolean {
  return msActive(c) || c.spend > 0 || c.clicks > 0 || c.impressions > 0;
}

/** Kampagnentabelle eines Microsoft-Kontos (Inhalt einer aufgeklappten Kachel). FTD/Trader = echte
 *  MS-Attribution der Tracker-Offline-Ziele je Kampagne (Microsoft bucht auf das Klick-Datum).
 *  Ohne Anzeigengruppen: die stehen schon in der Kampagnenliste darueber (Unterzeilen mit Impressionen, Operator 2026-10-26). */
function MsCampaignTable({ campaigns }: { campaigns: MsAdsCampaign[] }) {
  const agg = aggregate(campaigns);
  const rows = [...campaigns].sort((a, b) => b.spend - a.spend || b.clicks - a.clicks).slice(0, 15);
  const ftd = campaigns.reduce((s, c) => s + (c.goalFtd ?? 0), 0);
  const trader = campaigns.reduce((s, c) => s + (c.goalTrader ?? 0), 0);
  return (
    <>
      <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-border text-xs text-muted-foreground">
            <th className="px-1.5 py-1.5 text-left font-medium">Kampagne</th>
            <th className={TH}>Klicks</th>
            <th className={TH}>Ausgaben</th>
            <th className={TH}>Conv.</th>
            <th className={TH}>CPA</th>
            <th className={TH}>FTD</th>
            <th className={TH}>Trader</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.campaignId} className="border-t border-border/50">
              <td className={TD_NAME} title={c.campaignName}>
                {c.campaignName}
                {!msActive(c) && <span className="ml-1.5 text-[11px] font-normal text-muted-foreground">pausiert</span>}
              </td>
              <td className={TD}>{fmtInt(c.clicks)}</td>
              <td className={`${TD} font-medium`}>{fmtUsd(c.spend)}</td>
              <td className={TD}>{fmtInt(c.conversions)}</td>
              <td className={TD}>{c.costPerConversion != null ? fmtUsd(c.costPerConversion) : "—"}</td>
              <td className={`${TD} ${c.goalFtd ? "font-medium" : "text-muted-foreground"}`}>{c.goalFtd ? fmtInt(c.goalFtd) : "—"}</td>
              <td className={`${TD} ${c.goalTrader ? "font-medium" : "text-muted-foreground"}`}>{c.goalTrader ? fmtInt(c.goalTrader) : "—"}</td>
            </tr>
          ))}
          {campaigns.length === 0 && (
            <tr><td colSpan={7} className="py-4 text-center text-sm text-muted-foreground">Keine Kampagnen mit Daten im Zeitraum.</td></tr>
          )}
        </tbody>
        {campaigns.length > 0 && (
          <tfoot className="border-t border-border font-medium">
            <tr>
              <td className={TD_SUM}>Summe</td>
              <td className={TD}>{fmtInt(agg.clicks)}</td>
              <td className={TD}>{fmtUsd(agg.spend)}</td>
              <td className={TD}>{fmtInt(agg.conversions)}</td>
              <td className={TD}>{agg.costPerConv != null ? fmtUsd(agg.costPerConv) : "—"}</td>
              <td className={TD}>{fmtInt(ftd)}</td>
              <td className={TD}>{fmtInt(trader)}</td>
            </tr>
          </tfoot>
        )}
      </table>
      </div>
      <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
        <span>FTD/Trader: MS-Attribution (Klick-Datum)</span>
        {campaigns.length > rows.length && <span>…und {campaigns.length - rows.length} weitere</span>}
      </div>
    </>
  );
}

/** RoboForex je Kampagne bzw. Land (Relaunch 2026-10-01): Microsoft-Zahlen der RF-Kampagnen, verbunden mit den
 *  Tracker-Zahlen je Kampagnenname (Registrierungen und FTDs; Umsatz nicht mehr angezeigt, Operator 2026-10-02). Zeilen ohne
 *  Microsoft-Kampagne ("ohne Klick", "ChatGPT Ads") stehen darunter - so geht die Summe nicht verloren. */
function RoboforexKampagnenTable({ campaigns, kampagnen, fehler, hinweis = "Regs/FTDs: Tracker, Zuordnung über Zeit und Klick", nurMitDaten }: {
  campaigns: MsAdsCampaign[]; kampagnen: { rows: { kampagne: string; regs: number; ftds: number }[] } | null; fehler: string | null;
  hinweis?: string;
  /** Kampagnen ohne Klicks, Kosten und Conversions ausblenden (1xBet: 178 Kampagnen, die meisten ohne Klick; Operator 2026-10-10). */
  nurMitDaten?: boolean;
}) {
  const tr = new Map((kampagnen?.rows ?? []).map((r) => [r.kampagne, r]));
  const mitDaten = (c: MsAdsCampaign) => c.clicks > 0 || c.spend > 0 || !!tr.get(c.campaignName)?.regs || !!tr.get(c.campaignName)?.ftds;
  const ausgeblendet = nurMitDaten ? campaigns.filter((c) => !mitDaten(c)).length : 0;
  const ms = [...campaigns].filter((c) => !nurMitDaten || mitDaten(c)).sort((a, b) => b.spend - a.spend || b.clicks - a.clicks || a.campaignName.localeCompare(b.campaignName));
  const rest = (kampagnen?.rows ?? []).filter((r) => !ms.some((c) => c.campaignName === r.kampagne) && (r.regs || r.ftds));
  const zahl = (n: number | undefined) => (n ? fmtInt(n) : "—");
  const sum = { klicks: 0, spend: 0, regs: 0, ftds: 0 };
  for (const c of ms) { const t = tr.get(c.campaignName); sum.klicks += c.clicks; sum.spend += c.spend; sum.regs += t?.regs ?? 0; sum.ftds += t?.ftds ?? 0; }
  for (const r of rest) { sum.regs += r.regs; sum.ftds += r.ftds; }
  return (
    <>
      <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-border text-xs text-muted-foreground">
            <th className="px-1.5 py-1.5 text-left font-medium">Kampagne</th>
            <th className={TH}>Klicks</th>
            <th className={TH}>Ausgaben</th>
            <th className={TH}>Regs</th>
            <th className={TH}>FTDs</th>
          </tr>
        </thead>
        <tbody>
          {ms.map((c) => {
            const t = tr.get(c.campaignName);
            return (
              <tr key={c.campaignId} className="border-t border-border/50">
                <td className={TD_NAME} title={c.campaignName}>
                  {c.campaignName}
                  {!msActive(c) && <span className="ml-1.5 text-[11px] font-normal text-muted-foreground">pausiert</span>}
                </td>
                <td className={TD}>{fmtInt(c.clicks)}</td>
                <td className={`${TD} font-medium`}>{fmtUsd(c.spend)}</td>
                <td className={TD}>{zahl(t?.regs)}</td>
                <td className={TD}>{zahl(t?.ftds)}</td>
              </tr>
            );
          })}
          {rest.map((r) => (
            <tr key={r.kampagne} className="border-t border-border/50 text-muted-foreground">
              <td className={TD_NAME} title={r.kampagne}>{r.kampagne}</td>
              <td className={TD}>—</td>
              <td className={TD}>—</td>
              <td className={TD}>{zahl(r.regs)}</td>
              <td className={TD}>{zahl(r.ftds)}</td>
            </tr>
          ))}
          {ms.length === 0 && rest.length === 0 && (
            <tr><td colSpan={5} className="py-4 text-center text-sm text-muted-foreground">Keine Kampagnen mit Daten im Zeitraum.</td></tr>
          )}
        </tbody>
        {(ms.length > 0 || rest.length > 0) && (
          <tfoot className="border-t border-border font-medium">
            <tr>
              <td className={TD_SUM}>Summe</td>
              <td className={TD}>{fmtInt(sum.klicks)}</td>
              <td className={TD}>{fmtUsd(sum.spend)}</td>
              <td className={TD}>{fmtInt(sum.regs)}</td>
              <td className={TD}>{fmtInt(sum.ftds)}</td>
            </tr>
          </tfoot>
        )}
      </table>
      </div>
      <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
        <span>{hinweis}{ausgeblendet > 0 ? ` · ${ausgeblendet} ohne Klicks ausgeblendet` : ""}</span>
      </div>
      {fehler && <div className="mt-2 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">{fehler}</div>}
    </>
  );
}

/** Kennzahlen einer Meta- oder Google-Anzeigengruppe (MetaAdSet, GoogleAdGroup). */
type Gruppenzeile = Pick<MetaAdSet, "name" | "status" | "impressions" | "clicks" | "spend" | "trackerClicks" | "regs" | "ftds" | "cpa">;

/** Anzeigengruppe mit Daten im Zeitraum - aktive Gruppen laufender Kampagnen zaehlen auch mit 0 (neue Gruppen sichtbar). */
function gruppeZeigen(g: Gruppenzeile): boolean {
  return g.status === "ENABLED" || g.impressions > 0 || g.spend > 0 || g.trackerClicks > 0 || g.regs > 0 || g.ftds > 0 || g.cpa > 0;
}

/** Reihenfolge wie bei den Microsoft-Anzeigengruppen (Kosten, dann Klicks), bei Gleichstand F1 … F10 bzw. DE/FR/NL. */
function gruppenSortiert<T extends Gruppenzeile>(gruppen: T[]): T[] {
  return gruppen.filter(gruppeZeigen)
    .sort((a, b) => b.spend - a.spend || b.clicks - a.clicks || a.name.localeCompare(b.name, undefined, { numeric: true }));
}

/** Meta-Kampagnen mit ihren Anzeigengruppen (Operator 2026-10-22): Aufbau und Spalten wie MsCampaignTable. Kosten/Klicks aus
 *  Meta, Conv. (= Registrierungen), FTD und Trader aus dem Tracker (Zuordnung ueber Kampagnen-/Gruppen-ID im Klick) - Metas
 *  eigene "Leads" zaehlen nicht. Klicks ohne Kampagnen-ID stehen als eigene Zeile darunter. */
function MetaCampaignTable({ data, fehler }: { data: MetaCampaignsPayload | null; fehler: string | null }) {
  const alle = data?.campaigns ?? [];
  const cur = data?.account.currency ?? "USD";
  const rows = [...alle].sort((a, b) => (a.id ? 0 : 1) - (b.id ? 0 : 1) || b.spend - a.spend || b.clicks - a.clicks);
  const sum = alle.reduce((s, c) => ({ klicks: s.klicks + c.clicks, spend: s.spend + c.spend, regs: s.regs + c.regs, ftds: s.ftds + c.ftds, cpa: s.cpa + c.cpa }),
    { klicks: 0, spend: 0, regs: 0, ftds: 0, cpa: 0 });
  return (
    <>
      <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-border text-xs text-muted-foreground">
            <th className="px-1.5 py-1.5 text-left font-medium">Kampagne</th>
            <th className={TH}>Klicks</th>
            <th className={TH}>Ausgaben</th>
            <th className={TH}>Conv.</th>
            <th className={TH}>CPA</th>
            <th className={TH}>FTD</th>
            <th className={TH}>Trader</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id || c.name} className={cn("border-t border-border/50", !c.id && "text-muted-foreground")}>
              <td className={TD_NAME} title={c.name}>
                {c.name}
                {c.status && c.status !== "ENABLED" && <span className="ml-1.5 text-[11px] font-normal text-muted-foreground">pausiert</span>}
              </td>
              <td className={TD}>{c.id ? fmtInt(c.clicks) : "—"}</td>
              <td className={`${TD} font-medium`}>{c.id ? fmtMoney(c.spend, cur) : "—"}</td>
              <td className={TD}>{fmtInt(c.regs)}</td>
              <td className={TD}>{c.id && c.regs > 0 ? fmtMoney(c.spend / c.regs, cur) : "—"}</td>
              <td className={`${TD} ${c.ftds ? "font-medium" : "text-muted-foreground"}`}>{c.ftds ? fmtInt(c.ftds) : "—"}</td>
              <td className={`${TD} ${c.cpa ? "font-medium" : "text-muted-foreground"}`}>{c.cpa ? fmtInt(c.cpa) : "—"}</td>
            </tr>
          )).flatMap((zeile, i) => {
            // Anzeigengruppen direkt unter ihrer Kampagne (wie die Microsoft-Anzeigengruppen, Operator 2026-10-22).
            const c = rows[i]!;
            if (!c.adsets?.length) return [zeile];
            return [zeile, ...gruppenSortiert(c.adsets).map((g) => (
              <tr key={`as:${g.adsetId}`} className="text-[12px] text-muted-foreground" data-adgroup={g.name}>
                <td className={`${TD_NAME} pl-5`} title={g.name}>{g.name}</td>
                <td className={TD}>{fmtInt(g.clicks)}</td>
                <td className={TD}>{fmtMoney(g.spend, cur)}</td>
                <td className={TD}>{fmtInt(g.regs)}</td>
                <td className={TD}>{g.regs > 0 ? fmtMoney(g.spend / g.regs, cur) : "—"}</td>
                <td className={TD}>{g.ftds ? fmtInt(g.ftds) : "—"}</td>
                <td className={TD}>{g.cpa ? fmtInt(g.cpa) : "—"}</td>
              </tr>
            ))];
          })}
          {rows.length === 0 && (
            <tr><td colSpan={7} className="py-4 text-center text-sm text-muted-foreground">{data ? "Keine aktive Kampagne mit Daten im Zeitraum." : "Lädt …"}</td></tr>
          )}
        </tbody>
        {rows.length > 0 && (
          <tfoot className="border-t border-border font-medium">
            <tr>
              <td className={TD_SUM}>Summe</td>
              <td className={TD}>{fmtInt(sum.klicks)}</td>
              <td className={TD}>{fmtMoney(sum.spend, cur)}</td>
              <td className={TD}>{fmtInt(sum.regs)}</td>
              <td className={TD}>{sum.regs > 0 ? fmtMoney(sum.spend / sum.regs, cur) : "—"}</td>
              <td className={TD}>{fmtInt(sum.ftds)}</td>
              <td className={TD}>{fmtInt(sum.cpa)}</td>
            </tr>
          </tfoot>
        )}
      </table>
      </div>
      <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
        <span>Conv./FTD/Trader: Tracker, Zuordnung über Kampagnen-/Gruppen-ID im Klick</span>
      </div>
      {fehler && <div className="mt-2 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">{fehler}</div>}
    </>
  );
}

/** Schluessel einer aufgeklappten Google-Kampagne in openTiles (Tabelle und Partner-Detail teilen den Zustand). */
const gKampagneKey = (id: string) => `gkamp:${id}`;

/** Google-Ads-Kampagnen (Operator 2026-10-02): Zahlen aus der Google Ads API, Regs/FTDs aus dem Tracker (Zuordnung ueber
 *  cid={campaignid} im Final-URL-Suffix des Kontos). Klicks ohne Kampagnen-ID stehen als eigene Zeile darunter. Seit 2026-10-23
 *  klappt ein Klick auf die Kampagne ihre Google-Anzeigengruppen (agid=) darunter auf (Operator); Reddit/MGID/Taboola liefern keine. */
function GoogleCampaignTable({ data, fehler, offen, onToggle }: {
  data: GoogleCampaignsPayload | null; fehler: string | null; offen?: Set<string>; onToggle?: (key: string) => void;
}) {
  const rows = data?.campaigns ?? [];
  const cur = data?.account.currency ?? "EUR";
  const zahl = (n: number) => (n ? fmtInt(n) : "—");
  const sum = rows.reduce((s, c) => ({ klicks: s.klicks + c.clicks, spend: s.spend + c.spend, regs: s.regs + c.regs, ftds: s.ftds + c.ftds }),
    { klicks: 0, spend: 0, regs: 0, ftds: 0 });
  return (
    <>
      <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-border text-xs text-muted-foreground">
            <th className="px-1.5 py-1.5 text-left font-medium">Kampagne</th>
            <th className={TH}>Klicks</th>
            <th className={TH}>Ausgaben</th>
            <th className={TH}>Regs</th>
            <th className={TH}>FTDs</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => {
            const key = gKampagneKey(c.id);
            const klappbar = !!(c.id && c.adgroups?.length && onToggle);
            const auf = klappbar && !!offen?.has(key);
            return (
            <tr key={c.id || c.name} data-gcampaign={c.id || undefined}
              className={cn("border-t border-border/50", !c.id && "text-muted-foreground",
                klappbar && "cursor-pointer hover:bg-muted/30", auf && "bg-[#eef3fd] dark:bg-[#7aa2ff]/10")}
              {...(klappbar ? {
                onClick: () => onToggle!(key), tabIndex: 0, "aria-expanded": auf,
                onKeyDown: (e: { key: string; preventDefault: () => void }) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle!(key); } },
              } : {})}>
              <td className={TD_NAME} title={klappbar ? `${c.name} – Klick zeigt die Anzeigengruppen` : c.name}>
                {klappbar && <ChevronDown className={cn("mr-1 inline h-3.5 w-3.5 -translate-y-px text-muted-foreground transition-transform", auf && "rotate-180")} />}
                {c.name}
                {c.status && c.status !== "ENABLED" && <span className="ml-1.5 text-[11px] font-normal text-muted-foreground">pausiert</span>}
              </td>
              <td className={TD}>{c.id ? fmtInt(c.clicks) : "—"}</td>
              <td className={`${TD} font-medium`}>{c.id ? fmtMoney(c.spend, cur) : "—"}</td>
              <td className={TD}>{zahl(c.regs)}</td>
              <td className={TD}>{zahl(c.ftds)}</td>
            </tr>
            );
          }).flatMap((zeile, i) => {
            const c = rows[i]!;
            if (!c.adgroups?.length || !offen?.has(gKampagneKey(c.id))) return [zeile];
            return [zeile, ...gruppenSortiert(c.adgroups).map((g) => (
              <tr key={`ag:${g.adGroupId}`} className="text-[12px] text-muted-foreground" data-adgroup={g.name}>
                <td className={`${TD_NAME} pl-5`} title={g.name}>{g.name}</td>
                <td className={TD}>{fmtInt(g.clicks)}</td>
                <td className={TD}>{fmtMoney(g.spend, cur)}</td>
                <td className={TD}>{zahl(g.regs)}</td>
                <td className={TD}>{zahl(g.ftds)}</td>
              </tr>
            ))];
          })}
          {rows.length === 0 && (
            <tr><td colSpan={5} className="py-4 text-center text-sm text-muted-foreground">{data ? "Keine aktive Kampagne mit Daten im Zeitraum." : "Lädt …"}</td></tr>
          )}
        </tbody>
        {rows.length > 0 && (
          <tfoot className="border-t border-border font-medium">
            <tr>
              <td className={TD_SUM}>Summe</td>
              <td className={TD}>{fmtInt(sum.klicks)}</td>
              <td className={TD}>{fmtMoney(sum.spend, cur)}</td>
              <td className={TD}>{fmtInt(sum.regs)}</td>
              <td className={TD}>{fmtInt(sum.ftds)}</td>
            </tr>
          </tfoot>
        )}
      </table>
      </div>
      <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
        <span>Regs/FTDs: Tracker, Zuordnung über die Kampagnen-ID im Klick</span>
      </div>
      {fehler && <div className="mt-2 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">{fehler}</div>}
    </>
  );
}

// ---- Werbekonten: quadratische Kacheln in EINER Reihe ----

/** Zeile unter einem Werbekonto in der Kampagnenliste der Partner-Detailansicht. */
interface Unterzeile {
  key: string; name: string; impressions: number; clicks: number; spend: number; conversions: number; ftd?: number; trader?: number;
  /** Google (Operator 2026-10-23): Unterzeile = Kampagne, Klick klappt `gruppen` (ihre Anzeigengruppen) darunter auf;
   *  aufKey = Schluessel in openTiles. */
  aufKey?: string;
  gruppen?: Unterzeile[];
}

interface TileData {
  key: string;
  name: string;
  /** Plattform-Kürzel + Kampagnenzahl, z. B. "ChatGPT · 9" */
  sub: string;
  platform: "chatgpt" | "microsoft" | "google" | "meta" | "reddit" | "mgid" | "taboola";
  /** Produkt-Slug für das Logo statt des Namens (OpenAI-Konten, Operator 2026-09-10) */
  logo?: string;
  /** Text im gruenen eToro-Kreis statt eines Logos (Microsoft-Konten: "$1" … "$5"). */
  nummer?: string;
  /** Farbe des Nummernkreises, falls nicht eToro-Gruen (Werbekonten der Vergleichsseite: OpenAI-Schwarz). */
  nummerKlasse?: string;
  currency: string;
  spend: number;
  clicks: number;
  conversions: number;
  /** Impressionen im Zeitraum (Detailansicht der Partneruebersicht, Operator 2026-10-10). */
  impressions?: number;
  /** Geschätzte Höchstkosten (Klicks × aktuelles Gebot); null = keine Schätzung verfügbar. */
  maxCost?: number | null;
  error?: string | null;
  detail: ReactNode;
  /** Unterzeilen in der Kampagnenliste der Partner-Detailansicht (Operator 2026-10-20: Anzeigengruppen der eToro-FTD-Kampagne;
   *  2026-10-22: Meta-Anzeigengruppen unter "Facebook"). */
  unterzeilen?: Unterzeile[];
  /** FTD/Trader (= eToro-CPA) laut Microsoft-Zielattribution (Klick-Datum); Microsoft-Konten (Operator 2026-10-20) und seit
   *  2026-10-22 Meta (Tracker, Klick-Datum), sonst undefined. */
  ftd?: number;
  trader?: number;
}

/** Eine kleine quadratische Konto-Kachel: Name, Ausgaben, Klicks, Conversions. Klick
 *  klappt die Kampagnenliste unter der Reihe auf (Operator 2026-09-10: alle Konten in
 *  einer Reihe, halb so breit wie vorher). */
/** Kontonummer aus dem Registry-Namen ("eToro 3" -> "3"). Fehlt das Konto noch in der
 *  Registry, faellt der Name auf den Broker-Slug zurueck ("etoro9") - dann die Ziffern von
 *  dort nehmen, sonst zeigte die Kachel bloss den Anfangsbuchstaben. Marken ohne Nummer
 *  (RoboForex, AvaTrade) liefern weiterhin null und behalten ihr Logo. */
function accountNumber(name: string, slug?: string): string | null {
  // Nur die eToro-Konten tragen den gruenen Nummernkreis. Marken wie AvaTrade 2 behalten ihr
  // Logo, sonst stuenden zwei verschiedene Konten mit derselben "2" nebeneinander.
  const key = slug ?? "";
  if (!/^etoro\d*$/.test(key)) return null;
  const fromSlug = /^etoro(\d+)$/.exec(key);
  if (fromSlug) return fromSlug[1] ?? null;
  const m = /\s(\d+)$/.exec(name);
  return m?.[1] ?? "1";
}

function AccountTile({ t, open, onToggle, newRow }: { t: TileData; open: boolean; onToggle: () => void; newRow?: boolean }) {
  const num = t.nummer ?? accountNumber(t.name, t.logo);
  const Waehrung = t.currency === "USD" ? DollarSign : Euro;
  const klickText = fmtInt(t.clicks);
  const hasFigures = t.clicks > 0 || t.spend > 0 || t.conversions > 0;
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      data-tile={t.key}
      title={t.error ?? `${t.name} · ${t.sub}`}
      style={newRow ? { gridColumnStart: 1 } : undefined}
      className={cn(
        // Mindesthoehe = Kachel mit Klicks und Ausgaben: alle Kacheln der Seite gleich gross, auch leere.
        "flex min-h-[107px] min-w-0 flex-col rounded-xl border bg-card p-2 text-left shadow-sm transition-colors",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        open ? "border-primary ring-1 ring-primary/30" : "border-border hover:border-primary/50",
      )}
    >
      {/* Kopf: Nummernkreis (bzw. Produktlogo) + Aufklapp-Pfeil in abgerundetem Feld.
          Einfache Ansicht ohne Beschriftungen — nur Icon + Zahl (Operator 2026-09-15);
          Plattform und Kampagnenzahl stehen im Tooltip und im aufgeklappten Panel. */}
      <div className="flex items-start justify-between gap-1">
        {num
          ? (
            <span
              className={cn("flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[13px] font-medium leading-none text-white",
                t.nummerKlasse ?? "bg-[#53b84b]")}
              aria-label={t.name}
            >
              {num}
            </span>
          )
          : (
            <span className="flex h-7 w-7 shrink-0 items-center justify-center" aria-label={t.name}>
              <ProductIcon slug={t.logo ?? t.key} className="h-7 w-7" />
            </span>
          )}
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-muted/70">
          <ChevronDown className={cn("h-3 w-3 text-muted-foreground transition-transform", open && "rotate-180")} />
        </span>
      </div>

      {t.error && <div className="mt-1.5 truncate text-[11px] leading-tight text-destructive">Fehler</div>}

      {/* Kennzahlen: nur Werte ungleich 0 werden gezeigt (Operator 2026-09-15);
          nur die Klickzahl ist fett, alles andere normal. */}
      {hasFigures && <div className="my-1.5 h-px w-full bg-border" />}

      {t.clicks > 0 && (
        <div className="flex items-center gap-1">
          <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-md bg-emerald-500/10">
            <MousePointer2 className="h-2.5 w-2.5 fill-emerald-600 text-emerald-600" />
          </span>
          {/* Klickzahl immer ganz ausschreiben, nie mit "…" kuerzen (Operator 2026-09-30) - laengere Zahlen etwas
              kleiner, damit sie in die 71-px-Kachel passen. */}
          <span className={cn("whitespace-nowrap font-bold leading-none tabular-nums",
            klickText.length <= 3 ? "text-[15px]" : klickText.length <= 5 ? "text-[13px]" : "text-[11px]")}>{klickText}</span>
        </div>
      )}
      {t.spend > 0 && (
        <div className={cn("flex items-center gap-1", t.clicks > 0 && "mt-2")}>
          <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-md bg-muted/70">
            <Waehrung className="h-2.5 w-2.5 text-muted-foreground" />
          </span>
          <span className="whitespace-nowrap text-[11px] font-normal leading-none tabular-nums">{fmtAmount(t.spend)}</span>
        </div>
      )}
      {t.conversions > 0 && (
        <div className={cn("flex items-center gap-1.5", (t.clicks > 0 || t.spend > 0) && "mt-1.5")}>
          <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-md bg-muted/70">
            <BarChart3 className="h-2.5 w-2.5 text-muted-foreground" />
          </span>
          <span className="truncate text-[11px] font-normal leading-none tabular-nums">{fmtInt(t.conversions)}</span>
        </div>
      )}
    </button>
  );
}

function DetailPanel({ t, onClose }: { t: TileData; onClose: () => void }) {
  const cpa = t.conversions > 0 ? t.spend / t.conversions : null;
  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <CardTitle className="text-base">
            {t.name} <span className="text-xs font-normal text-muted-foreground">· {t.sub}</span>
          </CardTitle>
          <div className="flex items-center gap-3">
            <div className="whitespace-nowrap text-sm tabular-nums text-muted-foreground">
              {fmtMoney(t.spend, t.currency)} · {fmtInt(t.conversions)} Conv{cpa != null ? ` · ${fmtMoney(cpa, t.currency)} CPA` : ""}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={`${t.name} zuklappen`}
              className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent>{t.detail}</CardContent>
    </Card>
  );
}

/** Summe einer Plattform für die Kopfzeile der Kontenreihe (nur bei einheitlicher Währung). */
function platformTotal(tiles: TileData[]): string | null {
  const first = tiles[0];
  if (!first || tiles.some((t) => t.currency !== first.currency)) return null;
  const spend = tiles.reduce((s, t) => s + t.spend, 0);
  const conv = tiles.reduce((s, t) => s + t.conversions, 0);
  return `${fmtMoney(spend, first.currency)} · ${fmtInt(conv)} Conv.`;
}

/** Geschätzte Höchstkosten einer Plattform (Operator 2026-09-26): Summe aus Klicks je
 *  Anzeigengruppe × aktuellem Höchstgebot. Konten ohne Schätzung zählen mit ihren gemeldeten
 *  Kosten; null, wenn kein Konto eine Schätzung hat oder die Währungen gemischt sind. */
function platformMaxCost(tiles: TileData[]): { betrag: number; currency: string } | null {
  const first = tiles[0];
  if (!first || tiles.some((t) => t.currency !== first.currency)) return null;
  if (!tiles.some((t) => t.maxCost != null)) return null;
  return { betrag: tiles.reduce((s, t) => s + (t.maxCost ?? t.spend), 0), currency: first.currency };
}

const MAX_KOSTEN_ERKLAERUNG =
  "Geschätzte Höchstkosten: Klicks je Anzeigengruppe × deren aktuelles Höchstgebot, zusammengerechnet " +
  "über alle OpenAI-Kampagnen. OpenAI meldet die Kosten des laufenden Tages mit Verzögerung, die Klicks " +
  "fast sofort – die tatsächlichen Kosten liegen darunter. Bei längeren Zeiträumen mit geänderten Geboten " +
  "nur eine Näherung.";

const OPEN_TILES_KEY = "brokers:open-tiles";

function loadOpenTiles(): Set<string> {
  try {
    const raw = localStorage.getItem(OPEN_TILES_KEY);
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(Array.isArray(arr) ? arr.filter((x): x is string => typeof x === "string") : []);
  } catch {
    return new Set();
  }
}

function saveOpenTiles(open: Set<string>): void {
  try {
    localStorage.setItem(OPEN_TILES_KEY, JSON.stringify([...open]));
  } catch {
    /* privater Modus o.ä. — dann merkt sich der Browser die Auswahl eben nicht */
  }
}


// ---- Partneruebersicht (Operator 2026-10-09, nach Vorlage): alle Partner untereinander als Tabelle, je Zeile
// aufklappbar mit Laendern je Stufe und den Werbekonten ("Kampagnen") des Partners. ----

const PARTNER_OFFEN_KEY = "brokers:open-partner";
/** Konto $2 mit der eToro-FTD-Kampagne (Anzeigengruppen = Laender, Operator 2026-10-20). */
const FTD_KONTO = "189315822";
/** Spalten wie in der Vorlage, seit 2026-10-09 mit Klicks: Partner, Traffic, Klicks, Reg., FTD, Trader, Kosten/Reg., Kosten/FTD,
 *  Werbekosten, Pfeil. */
const PARTNER_GRID = "grid items-center gap-x-2 px-6 [grid-template-columns:2fr_2.1fr_0.68fr_0.52fr_0.55fr_0.6fr_1fr_1fr_1.1fr_16px]";
/** Blau der Vorlage fuer Pfeile, "Details" und die aufgeklappte Zeile. */
const VORLAGE_BLAU = "text-[#3563d6] dark:text-[#7aa2ff]";
const KAMPAGNEN_GRID = "grid items-center gap-x-3 px-3.5 [grid-template-columns:1.8fr_0.9fr_0.75fr_0.95fr_0.95fr]";
/** Wie KAMPAGNEN_GRID plus FTD und Trader (Abschnitte mit Microsoft-Konten, Operator 2026-10-20: sehen, in welcher Anzeigengruppe FTD/CPA entstehen). */
/** FTD-/Trader-Zelle der Kampagnenliste: Zahl hervorgehoben, 0/fehlend als Strich. */
function FtdZelle({ n }: { n?: number }) {
  return <div className={cn("tabular-nums", n ? "font-semibold text-foreground" : "text-muted-foreground")}>{n ? fmtInt(n) : "—"}</div>;
}
const KAMPAGNEN_GRID_FTD = "grid items-center gap-x-3 px-3.5 [grid-template-columns:1.8fr_0.8fr_0.65fr_0.85fr_0.7fr_0.5fr_0.55fr]";
type PartnerStufe = { key: string; label: string; count: number | null; rows: ConversionRow[] };
/** Traffic-Spalte: Plattformen der Werbekonten plus Kanaele ohne Konto-Kachel (Reddit: keine Kosten-API, nur Tracker). */
type Plattform = TileData["platform"] | "reddit";
const PLATTFORM_NAME: Record<Plattform, string> = { microsoft: "Microsoft", chatgpt: "OpenAI", google: "Google", meta: "Meta", reddit: "Reddit", mgid: "MGID", taboola: "Taboola" };
const PLATTFORM_QUELLE: Record<Plattform, string> = { microsoft: "microsoft", chatgpt: "openai", google: "google", meta: "meta", reddit: "reddit", mgid: "mgid", taboola: "taboola" };
const STUFE_LANG: Record<string, string> = { registration: "Registrierungen", ftd: "FTDs", cpa: "Trader" };

/** Betrag wie in der Vorlage: deutsches Komma, Cent nur unter 10 ("$1,22", "$316", "€0"). */
function fmtGeld(n: number, currency = "USD"): string {
  const sym = currency === "EUR" ? "€" : currency === "USD" ? "$" : `${currency} `;
  const cent = n > 0 && n < 10;
  return sym + n.toLocaleString("de-DE", { minimumFractionDigits: cent ? 2 : 0, maximumFractionDigits: cent ? 2 : 0 });
}

/** Partnername ohne Plattform ("eToro · Microsoft" -> "eToro", "OpenAI · Top 10 Vergleich" -> "Top 10 Vergleich"). */
function partnerName(titel: string): string {
  const teile = titel.split(" · ").filter((t) => !["Microsoft", "Google", "OpenAI", "ChatGPT", "Facebook", "Meta", "Reddit", "MGID", "Taboola"].includes(t));
  return teile.join(" · ") || titel;
}

const geraetName = (device?: string) => GERAETE.find((g) => g.key === device && g.key !== "unknown")?.label ?? "Gerät ?";
/** Herkunft einer Conversion ausserhalb der Karte ("$5 · FR | ava-first (8.10)"), null = Kampagne gehoert zur Karte. */
type Herkunft = (r: ConversionRow) => string | null;
type LandGeraet = { geo: string; device: string; count: number; herkunft: string | null };
/** Land x Geraet einer Stufe zusammengefasst (alle Quellen), meiste zuerst. Seit 2026-10-28 (Operator: "welches Land und
 *  welches Device") je Kombination ein Chip "FR · Desktop 1" statt nur "FR 1" - die Geraete-Chips daneben verraten nicht,
 *  welches Geraet zu welchem Land gehoert. Seit 2026-10-29 je Kombination auch getrennt nach fremder Kampagne. */
function landGeraetVon(rows: ConversionRow[], herkunft?: Herkunft): LandGeraet[] {
  const m = new Map<string, LandGeraet>();
  for (const r of rows) {
    const device = geraetName(r.device);
    const h = herkunft?.(r) ?? null;
    const k = `${r.geo}|${device}|${h ?? ""}`;
    const e = m.get(k) ?? { geo: r.geo, device, count: 0, herkunft: h };
    e.count += r.count;
    m.set(k, e);
  }
  const jeLand = new Map<string, number>();
  for (const e of m.values()) jeLand.set(e.geo, (jeLand.get(e.geo) ?? 0) + e.count);
  // Eigene Kampagnen vor fremden, damit "FR · Desktop 2" vor "FR · Desktop 1 · aus $5 ..." steht.
  return [...m.values()].filter((x) => x.count > 0)
    .sort((a, b) => (jeLand.get(b.geo)! - jeLand.get(a.geo)!) || a.geo.localeCompare(b.geo)
      || Number(a.herkunft !== null) - Number(b.herkunft !== null) || b.count - a.count);
}
/** "FR · Desktop"; ganz ohne Klick (weder Land noch Geraet) nur "?". */
const landGeraetText = (l: { geo: string; device: string }) =>
  l.geo === "unknown" && l.device === "Gerät ?" ? "?" : `${geoLabel(l.geo)} · ${l.device}`;
/** Tooltip der Partneruebersicht: eine Zeile je Land x Geraet, fremde Kampagnen mit "(aus ...)". */
const landGeraetTitel = (st: PartnerStufe | undefined, herkunft?: Herkunft) =>
  !st ? undefined : [st.label, ...landGeraetVon(st.rows, herkunft)
    .map((l) => `${landGeraetText(l)}: ${fmtInt(l.count)}${l.herkunft ? ` (aus ${l.herkunft})` : ""}`)].join("\n");

function loadOffenePartner(): Set<string> {
  try {
    const raw = localStorage.getItem(PARTNER_OFFEN_KEY);
    if (raw) return new Set(JSON.parse(raw) as string[]);
  } catch { /* privates Fenster o. ae. */ }
  return new Set(["etoro"]);
}

function saveOffenePartner(s: Set<string>): void {
  try { localStorage.setItem(PARTNER_OFFEN_KEY, JSON.stringify([...s])); } catch { /* egal */ }
}

/** Herkunft einer Stufe (Operator 2026-10-10): je Plattform Logo + Anzahl, im Stil der Geraete-Chips; Name im Tooltip. */
function QuellenChips({ rows }: { rows: { source: string; count: number }[] }) {
  const basis = (q: string) => (q.startsWith("microsoft") ? "microsoft" : q.startsWith("google") ? "google"
    : q.startsWith("meta") ? "meta" : q === "openai" ? "openai" : "other");
  const name: Record<string, string> = { microsoft: "Microsoft", google: "Google", meta: "Meta", openai: "OpenAI", other: "Ohne Werbeklick" };
  const n = new Map<string, number>();
  for (const r of rows) n.set(basis(r.source), (n.get(basis(r.source)) ?? 0) + r.count);
  const teile = ["microsoft", "openai", "google", "meta", "other"].filter((q) => (n.get(q) ?? 0) > 0);
  if (!teile.length) return null;
  return (
    <div className="flex shrink-0 items-center gap-1" data-quellen>
      {teile.map((q) => (
        <span key={q} title={`${name[q]}: ${n.get(q)}`} aria-label={`${name[q]} ${n.get(q)}`}
          className="inline-flex items-center gap-0.5 rounded border border-border bg-muted/60 px-1 py-0.5 text-[11px] font-medium text-foreground/80">
          {q === "other" ? "?" : <SourceIcon source={q} className="h-3 w-3" />}
          <span className="tabular-nums opacity-70">{n.get(q)}</span>
        </span>
      ))}
    </div>
  );
}

/** 1xPartners-Werte im Zeitraum (Detailansicht 1xBet): Einzahlungen, Firmengewinn, Provision + Stand des Portal-Abrufs. */
function OnexbetKasten({ daten, fehler }: { daten: OnexbetPayload | null; fehler: string | null }) {
  const s = daten?.summe;
  const stand = daten?.stand ? Math.round((Date.now() - new Date(daten.stand).getTime()) / 60000) : null;
  const zelle = (label: string, wert: number | undefined, titel: string, farbig?: boolean) => (
    <div className="min-w-0" title={titel}>
      <div className="text-[11.5px] text-muted-foreground">{label}</div>
      <div className={cn("mt-0.5 inline-block whitespace-nowrap rounded px-1 text-[14px] tabular-nums",
        farbig && wert !== undefined && wert < 0 && "bg-rose-500/10", farbig && wert !== undefined && wert > 0 && "bg-emerald-500/10")}>
        {wert === undefined ? "…" : `${wert < 0 ? "−" : ""}${fmtGeld(Math.abs(wert))}`}
      </div>
    </div>
  );
  return (
    <div className="mt-3 rounded-xl border border-border px-3.5 py-3" data-onexbet>
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-[13.5px] font-semibold">1xPartners</div>
        <div className={cn("text-[11.5px] tabular-nums", daten?.fehler || fehler ? "text-destructive" : "text-muted-foreground")}
          title={daten?.fehler ?? fehler ?? "Portal-Abruf alle 30 Minuten (Tracker)"}>
          {fehler ? "nicht erreichbar" : daten?.fehler ? "Abruf-Fehler" : stand !== null ? `Stand vor ${stand} Min.` : ""}
        </div>
      </div>
      <div className="mt-2 grid grid-cols-3 gap-3">
        {zelle("Einzahlungen", s?.einzahlungen, `Einzahlungen der Spieler im Zeitraum · ${s?.einzahler ?? 0} Einzahler`)}
        {zelle("Firmengewinn", s?.gewinn, "Gewinn von 1xBet mit unseren Spielern (Grundlage der RevShare)", true)}
        {zelle("Provision", s?.provision, "Unsere Provision laut 1xPartners (RevShare, negativ = Übertrag)", true)}
      </div>
    </div>
  );
}

/** Kampagnenname wie in der Vorlage: Microsoft-Konten "$1" -> "Microsoft 1". */
function kampagnenName(t: TileData): string {
  if (t.platform === "microsoft" && t.nummer && /^\$\d+$/.test(t.nummer)) return `Microsoft ${t.nummer.slice(1)}`;
  if (t.platform === "google") return "Google Ads";
  if (t.platform === "meta") return "Facebook";
  if (t.platform === "reddit") return "Reddit";
  if (t.platform === "mgid") return "MGID";
  if (t.platform === "taboola") return "Taboola";
  return t.name;
}

export function BrokersTab() {
  const [dateRange, setDateRange] = useState<DateRangeValue>(() => defaultDateRange("today"));
  const [openai, setOpenai] = useState<OpenaiCampaignsPayload | null>(null);
  const [openaiError, setOpenaiError] = useState<string | null>(null);
  const [conversions, setConversions] = useState<ConversionsPayload | null>(null);
  const [conversionsError, setConversionsError] = useState<string | null>(null);
  const [msCampaigns, setMsCampaigns] = useState<MsAdsCampaign[] | null>(null);
  // Anzeigengruppen der eToro-FTD-Kampagnen in $2 je Kampagnen-ID (Operator 2026-10-20: Laender sind dort Anzeigengruppen;
  // 2026-10-26: Kopie 571860191 ersetzt das pausierte Original, beide zeigen ihre Gruppen).
  const [ftdGruppen, setFtdGruppen] = useState<Record<string, MsAdsAdGroup[]> | null>(null);
  // RoboForex (Relaunch 2026-10-01): Regs/FTDs je Kampagne aus dem Tracker (Umsatz nicht mehr angezeigt, 2026-10-02).
  const [roboKampagnen, setRoboKampagnen] = useState<RoboforexKampagnenPayload | null>(null);
  // 1xBet-Portalwerte (Einzahlungen, Firmengewinn, Provision; Abruf alle 30 Min im Tracker).
  const [onexbet, setOnexbet] = useState<OnexbetPayload | null>(null);
  const [onexbetError, setOnexbetError] = useState<string | null>(null);
  const [roboError, setRoboError] = useState<string | null>(null);
  // Regs/FTDs je Kampagne fuer die uebrigen Sammelkonto-Marken (1xBet; Operator 2026-10-10 - vorher lasen sie die RoboForex-Zahlen).
  const [sammelKampagnen, setSammelKampagnen] = useState<Record<string, KampagnenPayload>>({});
  const [sammelFehler, setSammelFehler] = useState<Record<string, string>>({});
  // Google Ads (Operator 2026-10-02): eToro ueber Google als eigene Karte.
  const [google, setGoogle] = useState<GoogleCampaignsPayload | null>(null);
  const [googleError, setGoogleError] = useState<string | null>(null);
  // Eigene Google-Konten einzelner Marken (Product.googleKonto, Palmera seit 2026-11-04), je Konto-Schluessel.
  const [googleKonten, setGoogleKonten] = useState<Record<string, GoogleCampaignsPayload>>({});
  const [googleKontenFehler, setGoogleKontenFehler] = useState<Record<string, string>>({});
  // Meta (Operator 2026-10-13): eToro ueber Facebook/Instagram als eigene Karte, zwei Kampagnen (Desktop, Mobil).
  const [meta, setMeta] = useState<MetaCampaignsPayload | null>(null);
  const [metaError, setMetaError] = useState<string | null>(null);
  // Reddit (Operator 2026-10-15): Kosten/Klicks je Kampagne ueber den Tracker (Reddit-Ads-API), Conversions aus dem Tracker.
  const [reddit, setReddit] = useState<RedditCampaignsPayload | null>(null);
  const [redditError, setRedditError] = useState<string | null>(null);
  // MGID (Operator 2026-10-16): Kosten/Klicks je Kampagne ueber den Tracker (MGID-API, EUR), Conversions aus dem Tracker.
  const [mgid, setMgid] = useState<MgidCampaignsPayload | null>(null);
  const [mgidError, setMgidError] = useState<string | null>(null);
  // Taboola (Operator 2026-10-16): wie MGID.
  const [taboola, setTaboola] = useState<TaboolaCampaignsPayload | null>(null);
  const [taboolaError, setTaboolaError] = useState<string | null>(null);
  const [msError, setMsError] = useState<string | null>(null);
  // Stand der Microsoft-Zahlen: sie kommen aus einem zwischengespeicherten Report, ohne Zeitstempel
  // waere nicht erkennbar, ob gerade alte Daten angezeigt werden.
  const [msGeneratedAt, setMsGeneratedAt] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  // Aufgeklappte Kacheln — bleiben über Neuladen/Zeitraumwechsel erhalten.
  const [openTiles, setOpenTiles] = useState<Set<string>>(loadOpenTiles);
  // Partneruebersicht: aufgeklappte Partner (bleiben erhalten). Den Knopf "Details" (alte ausfuehrliche Karte) gibt es seit
  // 2026-10-10 nicht mehr (Operator).
  const [offenePartner, setOffenePartner] = useState<Set<string>>(loadOffenePartner);
  const togglePartner = (slug: string) =>
    setOffenePartner((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug); else next.add(slug);
      saveOffenePartner(next);
      return next;
    });
  const toggleTile = (key: string) =>
    setOpenTiles((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      saveOpenTiles(next);
      return next;
    });

  useEffect(() => {
    const params = toQueryParams(dateRange);
    let cancelled = false;
    const load = (initial: boolean) => {
      if (initial) {
        setOpenai(null); setOpenaiError(null);
        setConversions(null); setConversionsError(null);
        setMsCampaigns(null); setMsError(null);
        setRoboKampagnen(null); setRoboError(null);
        setOnexbet(null); setOnexbetError(null);
        setGoogle(null); setGoogleError(null);
        setGoogleKonten({}); setGoogleKontenFehler({});
      }
      api.openaiCampaigns(params)
        .then((r) => { if (!cancelled) { setOpenai(r); setOpenaiError(null); } })
        .catch((e: Error) => {
          if (!cancelled) {
            setOpenai((p) => p ?? { from: "", to: "", generatedAt: "", accounts: [], tracker: [] });
            setOpenaiError(e.message);
          }
        });
      api.conversions(params)
        .then((r) => { if (!cancelled) { setConversions(r); setConversionsError(r.errors?.length ? r.errors.join(" · ") : null); } })
        .catch((e: Error) => {
          if (!cancelled) {
            setConversions((p) => p ?? { from: "", to: "", generatedAt: "", rows: [] });
            setConversionsError(e.message);
          }
        });
      api.googleCampaigns(params)
        .then((r) => { if (!cancelled) { setGoogle(r); setGoogleError(null); } })
        .catch((e: Error) => { if (!cancelled) setGoogleError(`Google Ads: ${e.message}`); });
      for (const k of new Set(PRODUCTS.filter((p) => p.googleKonto && !p.hidden).map((p) => p.googleKonto!))) api.googleCampaigns(params, k)
        .then((r) => { if (!cancelled) { setGoogleKonten((v) => ({ ...v, [k]: r })); setGoogleKontenFehler(({ [k]: _, ...v }) => v); } })
        .catch((e: Error) => { if (!cancelled) setGoogleKontenFehler((v) => ({ ...v, [k]: `Google Ads: ${e.message}` })); });
      // Meta nur abfragen, solange ein sichtbares Produkt die Kachel zeigt (seit 2026-10-16 keins).
      if (PRODUCTS.some((p) => p.meta && !p.hidden)) api.metaCampaigns(params)
        .then((r) => { if (!cancelled) { setMeta(r); setMetaError(null); } })
        .catch((e: Error) => { if (!cancelled) setMetaError(`Meta: ${e.message}`); });
      // Reddit seit 2026-10-16 pausiert und ausgeblendet -> nur abfragen, solange ein sichtbares Produkt die Kachel zeigt.
      if (PRODUCTS.some((p) => p.reddit && !p.hidden)) api.redditCampaigns(params)
        .then((r) => { if (!cancelled) { setReddit(r); setRedditError(null); } })
        .catch((e: Error) => { if (!cancelled) setRedditError(`Reddit: ${e.message}`); });
      if (PRODUCTS.some((p) => p.mgid && !p.hidden)) api.mgidCampaigns(params)
        .then((r) => { if (!cancelled) { setMgid(r); setMgidError(null); } })
        .catch((e: Error) => { if (!cancelled) setMgidError(`MGID: ${e.message}`); });
      if (PRODUCTS.some((p) => p.taboola && !p.hidden)) api.taboolaCampaigns(params)
        .then((r) => { if (!cancelled) { setTaboola(r); setTaboolaError(null); } })
        .catch((e: Error) => { if (!cancelled) setTaboolaError(`Taboola: ${e.message}`); });
      api.onexbet(params)
        .then((r) => { if (!cancelled) { setOnexbet(r); setOnexbetError(null); } })
        .catch((e: Error) => { if (!cancelled) setOnexbetError(e.message); });
      for (const p of PRODUCTS) {
        if (!p.msKonto || !p.msPrefix || p.hidden || p.slug === "roboforex") continue;
        api.kampagnen(p.slug, params)
          .then((r) => { if (!cancelled) { setSammelKampagnen((m) => ({ ...m, [p.slug]: r })); setSammelFehler(({ [p.slug]: _, ...m }) => m); } })
          .catch((e: Error) => { if (!cancelled) setSammelFehler((m) => ({ ...m, [p.slug]: `${p.label} je Kampagne: ${e.message}` })); });
      }
      api.roboforexKampagnen(params)
        .then((r) => { if (!cancelled) setRoboKampagnen(r); })
        .catch((e: Error) => { if (!cancelled) setRoboError(`RoboForex je Kampagne: ${e.message}`); });
      api.msadsCampaigns(params)
        .then((r) => { if (!cancelled) { setMsCampaigns(r.campaigns.map(msMitRegs)); setMsError(null); setMsGeneratedAt(r.generatedAt ?? null); } })
        .catch((e: Error) => { if (!cancelled) { setMsCampaigns((p) => p ?? []); setMsError(e.message); } });
    };
    load(true);
    // Alle 3 Minuten nachladen. Der Server liefert den Cache sofort aus und frischt
    // ihn im Hintergrund auf, sobald er aelter als 5 Minuten ist — haeufigeres Holen
    // schliesst also die Luecke, ohne einen Seitenaufruf zu verlangsamen.
    const id = setInterval(() => load(false), 3 * 60 * 1000);
    const tick = setInterval(() => setNow(Date.now()), 30 * 1000);
    return () => { cancelled = true; clearInterval(id); clearInterval(tick); };
  }, [dateRange]);

  // FTD-Kampagne(n) in $2 erkennen (Name "eToro | FTD | ...") und ihre Anzeigengruppen holen. Laeuft bei jedem Neuladen der
  // Kampagnen mit (alle 3 Min); der Server cacht wie bei den Kampagnen.
  const ftdKampagnen = useMemo(() => (msCampaigns ?? []).filter((c) => c.accountId === FTD_KONTO && c.campaignName.startsWith("eToro | FTD")), [msCampaigns]);
  useEffect(() => {
    if (ftdKampagnen.length === 0) { setFtdGruppen(null); return; }
    let cancelled = false;
    Promise.all(ftdKampagnen.map((k) => api.msadsAdGroups({ ...toQueryParams(dateRange), accountId: FTD_KONTO, campaignId: k.campaignId })
      .then((r) => [r.campaignId, r.adGroups] as const)
      .catch(() => null)))   // ohne Gruppen bleibt die Kampagnenzeile; Fehler steht nicht im Weg
      .then((liste) => {
        if (cancelled) return;
        const m: Record<string, MsAdsAdGroup[]> = {};
        for (const e of liste) if (e) m[e[0]] = e[1];
        setFtdGruppen(m);
      });
    return () => { cancelled = true; };
  }, [ftdKampagnen, dateRange]);

  // Partner-Detailansicht: Anzeigengruppen aller FTD-Kampagnen nach Name zusammengefasst (Original und Kopie haben dieselben Namen).
  const ftdUnterzeilen = useMemo(() => {
    if (!ftdGruppen) return undefined;
    const m = new Map<string, MsAdsAdGroup>();
    for (const g of Object.values(ftdGruppen).flat()) {
      const e = m.get(g.adGroupName);
      if (!e) { m.set(g.adGroupName, { ...g }); continue; }
      e.impressions += g.impressions; e.clicks += g.clicks; e.spend += g.spend;
      e.goalSignups += g.goalSignups; e.goalFtd += g.goalFtd; e.goalTrader += g.goalTrader;
    }
    return [...m.values()].sort((a, b) => b.spend - a.spend || b.clicks - a.clicks);
  }, [ftdGruppen]);

  const msAgeMin = msGeneratedAt ? Math.max(0, Math.round((now - Date.parse(msGeneratedAt)) / 60000)) : null;

  // Microsoft-Konten $1-$5 aus der Registry in fester Reihenfolge. Seit 2026-10-20 (Operator) nur Konten mit einer aktiven
  // Kampagne oder mit Klicks/Kosten im Zeitraum - $1/$3/$4 sind komplett pausiert und erscheinen dann nicht mehr (fuer einen
  // frueheren Zeitraum, in dem sie Geld gekostet haben, tauchen sie samt Kosten automatisch wieder auf).
  const msTiles = useMemo<TileData[]>(() => Object.keys(ACCOUNT_LABEL)
    .sort((a, b) => accountRank(a) - accountRank(b))
    .filter((id) => msCampaigns === null || (msCampaigns ?? []).some((c) => c.accountId === id && msZeile(c)))
    .map((id) => {
      const alle = (msCampaigns ?? []).filter((c) => c.accountId === id);
      const items = alle.filter(msZeile);
      const agg = aggregate(items);
      const name = accountLabel(id, alle[0]?.accountName ?? "");
      return {
        key: `ms:${id}`,
        name,
        sub: `Microsoft · ${items.filter(msActive).length}`,
        platform: "microsoft",
        nummer: name,
        currency: "USD",
        spend: agg.spend, clicks: agg.clicks, conversions: agg.conversions, impressions: impr(items),
        error: msError && msCampaigns !== null && alle.length === 0 ? msError : null,
        ftd: items.reduce((s, c) => s + (c.goalFtd ?? 0), 0),
        trader: items.reduce((s, c) => s + (c.goalTrader ?? 0), 0),
        detail: <MsCampaignTable campaigns={items} />,
        // Kampagnenliste der Partner-Detailansicht: Anzeigengruppen der FTD-Kampagne mit Daten im Zeitraum unter dem Konto.
        unterzeilen: id === FTD_KONTO && ftdUnterzeilen
          ? ftdUnterzeilen.filter((g) => g.impressions > 0 || g.spend > 0 || g.goalSignups > 0 || g.goalFtd > 0 || g.goalTrader > 0).map((g) => ({
              key: g.adGroupName, name: g.adGroupName, impressions: g.impressions, clicks: g.clicks, spend: g.spend, conversions: g.goalSignups,
              ftd: g.goalFtd, trader: g.goalTrader,
            }))
          : undefined,
      };
    }), [msCampaigns, msError, ftdUnterzeilen]);

  // Microsoft-Konten je Marke (lib/accounts.ts ACCOUNT_MARKE): $5 gehoert seit 2026-10-07 zu AvaTrade, der Rest zu eToro.
  const msTilesDer = (slug: string) => msTiles.filter((t) => accountMarke(t.key.replace(/^ms:/, "")) === slug);

  // Kampagnen einer Marke in einem Sammelkonto (RoboForex: DL - $, Namensanfang "RF "), je Marke EINE Kachel.
  const sammelTiles = useMemo<Record<string, TileData>>(() => {
    const out: Record<string, TileData> = {};
    for (const p of PRODUCTS) {
      if (!p.msKonto || !p.msPrefix) continue;
      const alle = (msCampaigns ?? []).filter((c) => c.accountId === p.msKonto && c.campaignName.startsWith(p.msPrefix!));
      const items = alle.filter(msZeile);
      const agg = aggregate(items);
      out[p.slug] = {
        key: `ms:${p.msKonto}:${p.slug}`,
        name: alle[0]?.accountName?.trim() || p.msKontoName || p.msKonto,
        sub: `Microsoft · ${items.filter(msActive).length}`,
        platform: "microsoft",
        logo: p.slug,
        currency: "USD",
        spend: agg.spend, clicks: agg.clicks, conversions: agg.conversions, impressions: impr(items),
        error: msError && msCampaigns !== null ? msError : null,
        detail: p.slug === "roboforex"
          ? <RoboforexKampagnenTable campaigns={items} kampagnen={roboKampagnen} fehler={roboError} />
          : <RoboforexKampagnenTable campaigns={items} kampagnen={sammelKampagnen[p.slug] ?? null} fehler={sammelFehler[p.slug] ?? null}
              hinweis="Regs/FTDs: Tracker, Zuordnung über Sub-ID und Klick" nurMitDaten />,
      };
    }
    return out;
  }, [msCampaigns, msError, roboKampagnen, roboError, sammelKampagnen, sammelFehler]);

  // Weitere Microsoft-Konten derselben Marke (products.ts `msZusatz`, z. B. "1xBet 2"): je Konto eine eigene Kachel.
  // Regs/FTDs je Kampagne lassen sich nicht nach Konto trennen (gleiche Kampagnennamen) - sie stehen in der Hauptkachel.
  // Ohne aktive Kampagne und ohne Aktivitaet im Zeitraum faellt die Kachel weg (Operator 2026-10-13: "1xBet 2" pausiert).
  const sammelZusatz = useMemo<Record<string, TileData[]>>(() => {
    const out: Record<string, TileData[]> = {};
    for (const p of PRODUCTS) {
      if (!p.msZusatz?.length || !p.msPrefix) continue;
      out[p.slug] = p.msZusatz.flatMap((z): TileData[] => {
        const items = (msCampaigns ?? []).filter((c) => c.accountId === z.konto && c.campaignName.startsWith(p.msPrefix!)).filter(msZeile);
        if (items.length === 0) return [];
        const agg = aggregate(items);
        return [{
          key: `ms:${z.konto}:${p.slug}`,
          name: z.name,
          sub: `Microsoft · ${items.filter(msActive).length}`,
          platform: "microsoft",
          logo: p.slug,
          currency: "USD",
          spend: agg.spend, clicks: agg.clicks, conversions: agg.conversions, impressions: impr(items),
          error: msError && msCampaigns !== null ? msError : null,
          detail: <RoboforexKampagnenTable campaigns={items} kampagnen={null} fehler={null}
            hinweis={`Regs/FTDs: siehe ${p.msKontoName ?? "Hauptkonto"}`} nurMitDaten />,
        }];
      });
    }
    return out;
  }, [msCampaigns, msError]);

  // Google-Ads-Konto als EINE Kachel (alle Kampagnen des Kontos; heute nur eToro). Seit 2026-10-29 nur AKTIVE Kampagnen
  // (Operator: "die pausierten Kampagnen ausblenden", wie bei Meta) - in Kachel, Kontotabelle und Partner-Detail; die Zeile
  // "ohne Kampagnen-ID" (Klicks ohne cid) bleibt.
  const googleAktiv = useMemo<GoogleCampaignsPayload | null>(() => google && {
    ...google, campaigns: google.campaigns.filter((c) => !c.id || c.status === "ENABLED"),
  }, [google]);
  const baueGoogleTile = useCallback((googleAktiv: GoogleCampaignsPayload | null, googleError: string | null,
    standardKonto: string): TileData => {
    const items = (googleAktiv?.campaigns ?? []).filter((c) => c.id);
    return {
      key: `google:${googleAktiv?.account.id ?? standardKonto}`,
      name: googleAktiv?.account.name || "Google Ads",
      sub: `Google · ${items.filter((c) => c.status === "ENABLED").length}`,
      platform: "google",
      logo: "google",
      currency: googleAktiv?.account.currency ?? "EUR",
      spend: items.reduce((s, c) => s + c.spend, 0),
      clicks: items.reduce((s, c) => s + c.clicks, 0),
      impressions: impr(items),
      conversions: items.reduce((s, c) => s + c.conversions, 0),
      ftd: items.reduce((s, c) => s + c.ftds, 0),
      trader: items.reduce((s, c) => s + c.cpa, 0),
      error: googleError,
      detail: <GoogleCampaignTable data={googleAktiv} fehler={googleError} offen={openTiles} onToggle={toggleTile} />,
      // Kampagnenliste der Partner-Detailansicht (Operator 2026-10-23): unter "Google" die Kampagnen; Klick auf eine Kampagne
      // zeigt ihre Anzeigengruppen (aktive auch mit 0). Conv. = Tracker-Registrierungen wie in der Kontotabelle.
      unterzeilen: [...items].sort((a, b) => b.spend - a.spend || b.clicks - a.clicks || a.name.localeCompare(b.name)).map((c) => ({
        key: c.id, name: c.name, impressions: c.impressions, clicks: c.clicks, spend: c.spend, conversions: c.regs, ftd: c.ftds, trader: c.cpa,
        ...(c.adgroups?.length ? {
          aufKey: gKampagneKey(c.id),
          gruppen: gruppenSortiert(c.adgroups).map((g) => ({
            key: g.adGroupId, name: g.name, impressions: g.impressions, clicks: g.clicks, spend: g.spend, conversions: g.regs,
            ftd: g.ftds, trader: g.cpa,
          })),
        } : {}),
      })),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openTiles]);
  const googleTile = useMemo(() => baueGoogleTile(googleAktiv, googleError, "6138452317"), [baueGoogleTile, googleAktiv, googleError]);
  const googleKontoTiles = useMemo(() => Object.fromEntries(
    [...new Set(PRODUCTS.filter((p) => p.googleKonto).map((p) => p.googleKonto!))].map((k) => {
      const d = googleKonten[k];
      return [k, baueGoogleTile(d ? { ...d, campaigns: d.campaigns.filter((c) => !c.id || c.status === "ENABLED") } : null,
        googleKontenFehler[k] ?? null, k)];
    })) as Record<string, TileData>, [baueGoogleTile, googleKonten, googleKontenFehler]);

  // Meta-Konto als EINE Kachel "Facebook" (zwei Kampagnen: Desktop, Mobil). Conversions = Tracker-Registrierungen,
  // NICHT Metas "Leads" (Operator 2026-10-13).
  // Seit 2026-10-26 teilen sich mehrere Marken das Meta-Konto (AvaTrade, NordVPN): je Produkt nur die Kampagnen, deren Name
  // zu `metaKampagne` passt; ohne Filter alle (Kachel-Summen, Kopfzeilen).
  const metaTileFuer = useCallback((filter?: RegExp): TileData => {
    const items = (meta?.campaigns ?? []).filter((c) => c.id && (!filter || filter.test(c.name)));
    return {
      key: `meta:${meta?.account.id ?? "1376217574592755"}${filter ? ":" + filter.source : ""}`,
      name: "Facebook",
      sub: `Meta · ${items.filter((c) => c.status === "ENABLED").length}`,
      platform: "meta",
      logo: "meta",
      currency: meta?.account.currency ?? "USD",
      spend: items.reduce((s, c) => s + c.spend, 0),
      clicks: items.reduce((s, c) => s + c.clicks, 0),
      impressions: impr(items),
      conversions: items.reduce((s, c) => s + c.regs, 0),
      ftd: items.reduce((s, c) => s + c.ftds, 0),
      trader: items.reduce((s, c) => s + c.cpa, 0),
      error: metaError,
      detail: <MetaCampaignTable data={meta ? { ...meta, campaigns: items } : null} fehler={metaError} />,
      // Kampagnenliste der Partner-Detailansicht: Anzeigengruppen beider Kampagnen unter "Facebook" (wie bei Microsoft 2).
      unterzeilen: items.some((c) => c.adsets)
        ? gruppenSortiert(items.flatMap((c) => c.adsets ?? [])).map((g) => ({
            key: g.adsetId, name: g.name, impressions: g.impressions, clicks: g.clicks, spend: g.spend, conversions: g.regs,
            ftd: g.ftds, trader: g.cpa,
          }))
        : undefined,
    };
  }, [meta, metaError]);
  const metaTile = useMemo<TileData>(() => metaTileFuer(), [metaTileFuer]);

  // Reddit-Konto als EINE Kachel "Reddit" (vier Laender-Kampagnen). Conversions = Tracker-Registrierungen.
  const redditTile = useMemo<TileData>(() => {
    const items = (reddit?.campaigns ?? []).filter((c) => c.id);
    return {
      key: `reddit:${reddit?.account.id ?? "a2_dg0t8u5ulm65"}`,
      name: "Reddit",
      sub: `Reddit · ${items.filter((c) => c.status === "ENABLED").length}`,
      platform: "reddit",
      logo: "reddit",
      currency: reddit?.account.currency ?? "USD",
      spend: items.reduce((s, c) => s + c.spend, 0),
      clicks: items.reduce((s, c) => s + c.clicks, 0),
      impressions: impr(items),
      conversions: items.reduce((s, c) => s + c.regs, 0),
      error: redditError,
      detail: <GoogleCampaignTable data={reddit} fehler={redditError} />,
    };
  }, [reddit, redditError]);

  // MGID-Konto als EINE Kachel "MGID" (Kampagnen DE/AT). Conversions = Tracker-Registrierungen.
  const mgidTile = useMemo<TileData>(() => {
    const items = (mgid?.campaigns ?? []).filter((c) => c.id);
    return {
      key: `mgid:${mgid?.account.id ?? "878809"}`,
      name: "MGID",
      sub: `MGID · ${items.filter((c) => c.status === "ENABLED").length}`,
      platform: "mgid",
      logo: "mgid",
      currency: mgid?.account.currency ?? "EUR",
      spend: items.reduce((s, c) => s + c.spend, 0),
      clicks: items.reduce((s, c) => s + c.clicks, 0),
      impressions: impr(items),
      conversions: items.reduce((s, c) => s + c.regs, 0),
      error: mgidError,
      detail: <GoogleCampaignTable data={mgid} fehler={mgidError} />,
    };
  }, [mgid, mgidError]);

  // Taboola-Konto als EINE Kachel "Taboola" (Kampagnen DE/AT). Conversions = Tracker-Registrierungen.
  const taboolaTile = useMemo<TileData>(() => {
    const items = (taboola?.campaigns ?? []).filter((c) => c.id);
    return {
      key: `taboola:${taboola?.account.id ?? "taboolaaccount-adsataraxmarketingcom"}`,
      name: "Taboola",
      sub: `Taboola · ${items.filter((c) => c.status === "ENABLED").length}`,
      platform: "taboola",
      logo: "taboola",
      currency: taboola?.account.currency ?? "EUR",
      spend: items.reduce((s, c) => s + c.spend, 0),
      clicks: items.reduce((s, c) => s + c.clicks, 0),
      impressions: impr(items),
      conversions: items.reduce((s, c) => s + c.regs, 0),
      error: taboolaError,
      detail: <GoogleCampaignTable data={taboola} fehler={taboolaError} />,
    };
  }, [taboola, taboolaError]);

  // Kostenbasis je Marke (Operator 2026-09-14/-29): die Ausgaben ALLER Werbekonten der Marke im
  // Zeitraum, in USD (OpenAI-Konten rechnen in EUR, siehe lib/fx.ts), auch aus pausierten und archivierten
  // Kampagnen - die haben im Zeitraum ebenfalls Geld gekostet. eToro zaehlt seit 2026-10-01 zusaetzlich die
  // Microsoft-Konten $1-$5 (USD). Achtung: OpenAI bucht Ausgaben mit bis zu einem Tag Verzögerung.
  const eurUsd = useEurUsd();
  const spendUsd = useMemo(() => {
    const out: Record<string, number> = {};
    for (const a of openai?.accounts ?? []) {
      const marke = markeDesKontos(a.broker);
      if (!marke || !zaehltOpenai(marke)) continue;
      out[marke] = (out[marke] ?? 0) + toUsd(a.campaigns.reduce((x, c) => x + c.spend, 0), a.currency, eurUsd);
    }
    for (const p of PRODUCTS) {
      const ms = msTilesDer(p.slug);
      if (ms.length && MARKEN.includes(p.slug)) out[p.slug] = (out[p.slug] ?? 0) + ms.reduce((x, t) => x + t.spend, 0);
      const st = sammelTiles[p.slug];
      if (st && MARKEN.includes(p.slug)) out[p.slug] = (out[p.slug] ?? 0) + st.spend;
      for (const z of sammelZusatz[p.slug] ?? []) if (MARKEN.includes(p.slug)) out[p.slug] = (out[p.slug] ?? 0) + z.spend;
      if (p.google && MARKEN.includes(p.slug)) out[p.slug] = (out[p.slug] ?? 0) + toUsd(googleTile.spend, googleTile.currency, eurUsd);
      const gk = p.googleKonto ? googleKontoTiles[p.googleKonto] : undefined;
      if (gk && MARKEN.includes(p.slug)) out[p.slug] = (out[p.slug] ?? 0) + toUsd(gk.spend, gk.currency, eurUsd);
      if (p.meta && MARKEN.includes(p.slug)) { const mt = metaTileFuer(p.metaKampagne); out[p.slug] = (out[p.slug] ?? 0) + toUsd(mt.spend, mt.currency, eurUsd); }
      if (p.reddit && MARKEN.includes(p.slug)) out[p.slug] = (out[p.slug] ?? 0) + toUsd(redditTile.spend, redditTile.currency, eurUsd);
      if (p.mgid && MARKEN.includes(p.slug)) out[p.slug] = (out[p.slug] ?? 0) + toUsd(mgidTile.spend, mgidTile.currency, eurUsd);
      if (p.taboola && MARKEN.includes(p.slug)) out[p.slug] = (out[p.slug] ?? 0) + toUsd(taboolaTile.spend, taboolaTile.currency, eurUsd);
    }
    return out;
  }, [openai, eurUsd, msTiles, sammelTiles, sammelZusatz, googleTile, googleKontoTiles, metaTileFuer, redditTile, mgidTile, taboolaTile]);

  /** Herkunft einer Microsoft-Conversion, deren Kampagne nicht zur Karte gehoert (Operator 2026-10-29: eToro-FTD aus der
   *  AvaTrade-Kampagne in $5 stand ohne Hinweis in der eToro-Karte) -> "$5 · FR | ava-first (8.10)", sonst null. Zur Karte
   *  gehoeren die Konten ihrer Marke ($1-$4 eToro, $5 AvaTrade) bzw. im Sammelkonto die Kampagnen mit ihrem Namensanfang.
   *  Unbekannte Kampagne (kein cid/utm_campaign, nicht in der Kampagnenliste) -> kein Hinweis. */
  const fremdeKampagne = useCallback((slug: string, r: ConversionRow): string | null => {
    const camp = r.campaign ?? "";
    if (!camp || !r.source.startsWith("microsoft")) return null;
    const cid = camp.startsWith("cid:") ? camp.slice(4) : null;
    // Kampagnennamen wiederholen sich in den Kopiekonten ($3/$4 = $2): gehoert irgendein Treffer zur Karte, ist sie eigen.
    const treffer = (msCampaigns ?? []).filter((c) => (cid ? c.campaignId === cid : c.campaignName === camp));
    if (treffer.length === 0) return null;
    const p = PRODUCTS.find((x) => x.slug === slug);
    const eigen = (k: MsAdsCampaign) =>
      p?.msPrefix && (p.msKonto === k.accountId || p.msZusatz?.some((z) => z.konto === k.accountId))
        ? k.campaignName.startsWith(p.msPrefix)
        : Boolean(ACCOUNT_LABEL[k.accountId]) && accountMarke(k.accountId) === slug;
    if (treffer.some(eigen)) return null;
    const k = treffer[0]!;
    return `${accountLabel(k.accountId, k.accountName)} · ${k.campaignName}`;
  }, [msCampaigns]);

  // Conversions je Marke (alle Werbekonten der Marke zusammen). Nur Klicks aus OpenAI-Kampagnen - Fremdverkehr
  // wie brokerchampion bleibt draussen -, ausser bei Marken mit `allSources` (eToro: Microsoft + OpenAI).
  const markenCells = useMemo(() => {
    const rows = conversions?.rows ?? [];
    const markenCells: Record<string, MarkenCell[]> = {};
    for (const p of PRODUCTS) {
      if (!MARKEN.includes(p.slug) || p.vergleich) continue;
      // Fremde Kampagnen erkennt erst die Microsoft-Kampagnenliste: bis dahin "…" statt kurz zu hoher Zahlen.
      const pending = conversions === null || (p.nurEigeneKampagnen && msCampaigns === null);
      const spend = spendUsd[p.slug] ?? 0;
      const quelle = (r: ConversionRow) => (p.allSources
        || (p.quellenAusser ? !p.quellenAusser.includes(r.source) : p.quellen ? p.quellen.includes(r.source) : r.source === "openai"))
        && !(p.nurEigeneKampagnen && fremdeKampagne(p.slug, r));
      markenCells[p.slug] = p.stages.map((st) => {
        const rs = rows.filter((r) => brokerPasst(r.broker, p.broker ?? p.slug) && r.stage === st.key && quelle(r));
        const count = rs.reduce((s, r) => s + r.count, 0);
        const value = rs.reduce((s, r) => s + r.value, 0);
        // Gesamtausgaben der Marke geteilt durch die Gesamtzahl der Stufe.
        const per = st.short;
        // Wertzeile: Trader-Stufe (AvaTrade, eToro) und Stufen mit Provisionsbetrag (NordVPN, Deel, HighLevel).
        // Sign-ups mit interner Schaetzung je Land (eToro, AvaTrade): Tageswert = Sign-ups x Wert je Land.
        // Seit 2026-10-05 zusaetzlich mal Geraetefaktor (Klick-Geraet der Zeile); im Hover die Aufteilung je Geraet.
        const rw = st.key === "registration" ? p.regWert : undefined;
        const gf = (r: ConversionRow) => (rw?.geraet?.[r.device ?? "unknown"] ?? 1) * (rw?.quelle?.[r.source] ?? 1);
        const landWert = (r: ConversionRow) => rw!.werte[r.geo.toLowerCase()] ?? rw!.standard;
        const schaetz = rw ? rs.reduce((s, r) => s + r.count * landWert(r) * gf(r), 0) : 0;
        const schaetzSpread = rw?.mitSpread ? rs.reduce((s, r) => s + r.count * (rw.mitSpread![r.geo.toLowerCase()] ?? landWert(r)) * gf(r), 0) : null;
        const jeGeraet = rw?.geraet ? GERAETE.map(({ key, label }) => {
          const name = key === "unknown" ? "ohne Gerät" : label;
          const g = rs.filter((r) => (r.device ?? "unknown") === key);
          const n = g.reduce((s, r) => s + r.count, 0);
          return n ? `${name}: ${fmtInt(n)} × ≈ ${fmtUsd(g.reduce((s, r) => s + r.count * landWert(r) * gf(r), 0) / n)} = ${fmtUsd(g.reduce((s, r) => s + r.count * landWert(r) * gf(r), 0))}` : null;
        }).filter(Boolean).join("\n") : "";
        const wert = rw
          ? (count > 0 ? {
              betrag: fmtUsd(schaetz), faktor: spend ? `${(schaetz / spend).toFixed(1)}×` : null,
              titel: rw.hinweis + (jeGeraet ? `\n\n${jeGeraet}` : "")
                + (schaetzSpread != null ? `\nMit Spread-Anteil (35 %): ≈ ${fmtUsd(schaetzSpread)}${spend ? ` (${(schaetzSpread / spend).toFixed(1)}×)` : ""}` : ""),
            } : null)
          : st.key !== "cpa" && !st.provision ? undefined
          : value > 0 ? { betrag: fmtUsd(value), faktor: spend ? `${(value / spend).toFixed(1)}×` : null } : null;
        // Vorstufe (HighLevel-Leads): eigene Zahl in der Zusatzzeile, zaehlt nicht als Sign-up.
        const vs = st.vorstufe;
        const nVor = vs ? rows.filter((r) => brokerPasst(r.broker, p.broker ?? p.slug) && r.stage === vs.key && quelle(r))
          .reduce((s, r) => s + r.count, 0) : 0;
        const vorstufe = vs ? {
          label: vs.label, anzahl: pending ? "…" : fmtInt(nVor),
          kosten: spend && nVor ? `≈ ${fmtUsd(spend / nVor)} / ${vs.short}` : null,
        } : undefined;
        return {
          key: st.key, label: st.short, labelGross: st.labelCompact ?? st.label, titel: st.label,
          display: pending ? "…" : fmtInt(count),
          // Kostenzeile nur mit dem Betrag, damit sie in die schmalen Spalten passt; die Erklaerung steht im Tooltip.
          sub: spend && count ? fmtUsd(spend / count) : "–", subTitel: spend && count ? `≈ ${fmtUsd(spend / count)} je ${per}` : undefined,
          badges: rs, wert, vorstufe,
        };
      });
    }
    return markenCells;
  }, [conversions, spendUsd, fremdeKampagne, msCampaigns]);

  // Karte "OpenAI · Top 10 Vergleich" (Operator 2026-10-06): je Zielmarke die Conversions ihrer Vergleichs-Broker
  // (etorovgl, etorovgl1, etorovgl2 bzw. avatradevgl…), getrennt gezaehlt. Die Ausgaben der beiden Werbekonten lassen sich
  // keiner Zielmarke zuordnen - Kosten je Stufe daher ueber beide Marken zusammen.
  const vergleichDaten = useMemo(() => {
    const rows = conversions?.rows ?? [];
    const pending = conversions === null;
    const out: Record<string, VergleichDaten> = {};
    for (const p of PRODUCTS) {
      if (!p.vergleich || !MARKEN.includes(p.slug)) continue;
      const spend = spendUsd[p.slug] ?? 0;
      const quelle = (r: ConversionRow) => p.allSources || (p.quellen ? p.quellen.includes(r.source) : r.source === "openai");
      const zeilen = p.vergleich.map((v) => ({
        marke: v.marke, label: v.label,
        cells: p.stages.map((st): VergleichZelle => {
          const rs = rows.filter((r) => brokerPasst(r.broker, v.praefix) && r.stage === st.key && quelle(r));
          const count = rs.reduce((x, r) => x + r.count, 0);
          return {
            key: st.key, label: st.short, titel: `${v.label} · ${st.label}`, count,
            value: rs.reduce((x, r) => x + r.value, 0), display: pending ? "…" : fmtInt(count), badges: rs,
          };
        }),
      }));
      const marken = p.vergleich.map((v) => v.label).join(" + ");
      const kosten = p.stages.map((st, i) => {
        const n = zeilen.reduce((x, z) => x + (z.cells[i]?.count ?? 0), 0);
        return spend && n
          ? { key: st.key, sub: fmtUsd(spend / n), titel: `≈ ${fmtUsd(spend / n)} je ${st.short} (Ausgaben beider Werbekonten / ${st.short} ${marken})` }
          : { key: st.key, sub: "–" };
      });
      const wert = zeilen.reduce((x, z) => x + z.cells.filter((c) => c.key === "cpa").reduce((y, c) => y + c.value, 0), 0);
      out[p.slug] = {
        zeilen, kosten,
        wert: wert > 0 ? { betrag: fmtUsd(wert), faktor: spend ? `${(wert / spend).toFixed(1)}×` : null, titel: WERT_VERGLEICH_ERKLAERUNG } : null,
      };
    }
    return out;
  }, [conversions, spendUsd]);

  const openaiTiles = useMemo<TileData[]>(() => {
    // Gesperrte Konten (z. B. AvaTrade 2 seit 2026-09-29) liefern nicht mehr aus - keine Kachel (Operator). Ihre Ausgaben
    // im Zeitraum zaehlen in spendUsd weiter mit, ihre Conversions ohnehin (Slug-Praefix der Marke).
    const oa = (openai?.accounts ?? []).filter((a) => !productHidden(a.broker) && a.status !== "suspended")
      .sort((a, b) => productRank(a.broker) - productRank(b.broker));
    const errs = openai?.errors ?? [];
    return oa.map((a) => {
      const active = a.campaigns.filter(openaiActive);
      // Kachel zeigt die GESAMTKOSTEN des Kontos im Zeitraum - auch aus pausierten und
      // archivierten Kampagnen. Die haben im Zeitraum Geld gekostet, und nach einer
      // Pausierung verschwand es sonst aus der Anzeige (Operator 2026-09-18).
      const agg = aggregate(a.campaigns);
      const mine = errs.filter((e) => e.startsWith(`${a.broker}:`));
      // Werbekonten der Vergleichsseite: Kontonummer im schwarzen OpenAI-Kreis; ihre Conversions laufen unter
      // etorovgl<n> UND avatradevgl<n> (Alias desselben Kontos).
      const konto = vergleichKonto(a.broker);
      return {
        key: `openai:${a.broker}`,
        name: productLabel(a.broker),
        sub: `OpenAI · ${active.length}`,
        platform: "chatgpt",
        logo: a.broker,
        ...(konto ? { nummer: konto, nummerKlasse: "bg-[#111111]" } : {}),
        currency: a.currency,
        spend: agg.spend, clicks: agg.clicks, conversions: agg.conversions, impressions: impr(a.campaigns),
        maxCost: a.campaigns.some((c) => c.maxCost != null)
          ? a.campaigns.reduce((s, c) => s + (c.maxCost ?? c.spend), 0) : null,
        error: mine.length ? mine.join(" · ") : null,
        detail: (
          <OpenaiCampaignTable
            account={a}
            tracker={(openai?.tracker ?? []).filter((t) => t.broker === a.broker || (konto !== null && vergleichKonto(t.broker) === konto))}
            errors={mine.length ? mine : undefined}
          />
        ),
      };
    });
  }, [openai]);
  // Fehler, die keiner Kachel zuzuordnen sind (Endpunkt selbst oder Konto ohne accounts[]-Eintrag).
  const chatgptErrors = useMemo(() => {
    const brokers = new Set((openai?.accounts ?? []).map((a) => a.broker));
    const stray = (openai?.errors ?? []).filter((e) => ![...brokers].some((b) => e.startsWith(`${b}:`)) && !PRODUCTS.some((p) => p.hidden && e.startsWith(`${p.slug}:`)));
    return [openaiError, ...stray].filter((x): x is string => Boolean(x));
  }, [openai, openaiError]);

  /** Werbekonten einer Marke: zuerst Microsoft (eToro $1-$5), dann OpenAI. Die eToro-Konten bei OpenAI sind seit
   *  2026-09-29 pausiert - sie erscheinen nur, wenn sie im Zeitraum noch etwas getan haben. */
  const kontenDerMarke = (slug: string): TileData[] => {
    const p = PRODUCTS.find((x) => x.slug === slug);
    // Marken mit Microsoft-Konten (eToro, RoboForex): ihre pausierten OpenAI-Konten nur mit Aktivitaet im Zeitraum.
    const nurAktiv = Boolean(p?.microsoft || p?.msKonto);
    const oa = !zaehltOpenai(slug) ? [] : openaiTiles.filter((t) => kontoPraefixe(slug).some((pf) => brokerPasst(t.logo ?? "", pf))
      && (!nurAktiv || t.spend > 0 || t.clicks > 0 || t.conversions > 0));
    return [...msTilesDer(slug), ...(sammelTiles[slug] ? [sammelTiles[slug]!] : []), ...(sammelZusatz[slug] ?? []), ...(p?.google ? [googleTile] : []), ...(p?.googleKonto && googleKontoTiles[p.googleKonto] ? [googleKontoTiles[p.googleKonto]!] : []), ...(p?.meta ? [metaTileFuer(p.metaKampagne)] : []), ...(p?.reddit ? [redditTile] : []), ...(p?.mgid ? [mgidTile] : []), ...(p?.taboola ? [taboolaTile] : []), ...oa];
  };

  /** Kopfzeile einer Kontengruppe: Summen je Plattform, MS-Stand und geschaetzte Hoechstkosten bei OpenAI.
   *  Je Plattform ein Block, der nie in sich umbricht - reicht die Breite nicht, steht jede Plattform in einer eigenen
   *  Zeile. In den schmalen Test-Spalten stehen Summe und Hoechstkosten untereinander. */
  const kopfzeile = (slug: string, tiles: TileData[], schmal: boolean) => {
    const ms = tiles.filter((t) => t.platform === "microsoft");
    const oa = tiles.filter((t) => t.platform === "chatgpt");
    const gg = tiles.filter((t) => t.platform === "google");
    const mt = tiles.filter((t) => t.platform === "meta");
    const rd = tiles.filter((t) => t.platform === "reddit");
    const mg = tiles.filter((t) => t.platform === "mgid");
    const tb = tiles.filter((t) => t.platform === "taboola");
    // Waehrend der Microsoft-Bericht noch laedt, keine "$0.00" zeigen.
    const msSumme = ms.length && msCampaigns !== null ? platformTotal(ms) : null;
    const googleSumme = gg.length && google !== null ? platformTotal(gg) : null;
    const metaSumme = mt.length && meta !== null ? platformTotal(mt) : null;
    const redditSumme = rd.length && reddit !== null ? platformTotal(rd) : null;
    const mgidSumme = mg.length && mgid !== null ? platformTotal(mg) : null;
    const taboolaSumme = tb.length && taboola !== null ? platformTotal(tb) : null;
    const oaSumme = oa.length ? platformTotal(oa) : null;
    const max = platformMaxCost(oa);
    const maxText = max && <>max. Kosten ≈ {fmtMoney(max.betrag, max.currency)}</>;
    const msStand = msAgeMin !== null && (
      <span className={cn("whitespace-nowrap", msAgeMin > 15 && "text-amber-600 dark:text-amber-500")}
        title={`Microsoft-Zahlen erzeugt: ${new Date(msGeneratedAt!).toLocaleString()}`}>
        {schmal ? "" : " · "}MS-Stand: {msAgeMin < 1 ? "gerade eben" : `vor ${msAgeMin} Min.`}
      </span>
    );
    return (
      <div className={cn("px-0.5 text-[11px] leading-snug tabular-nums text-muted-foreground", schmal ? "flex flex-col" : "flex flex-wrap gap-x-3")} data-kopf={slug}>
        {msSumme && (
          <span className="whitespace-nowrap">
            Microsoft {msSumme}
            {msAgeMin !== null && !schmal && msStand}
          </span>
        )}
        {/* Schmale Karten (seit 2026-10-06 vier je Reihe): MS-Stand in eigener Zeile, sonst wird er abgeschnitten. */}
        {msSumme && msAgeMin !== null && schmal && msStand}
        {googleSumme && <span className="whitespace-nowrap">Google {googleSumme}</span>}
        {metaSumme && <span className="whitespace-nowrap">Meta {metaSumme}</span>}
        {redditSumme && <span className="whitespace-nowrap">Reddit {redditSumme}</span>}
        {mgidSumme && <span className="whitespace-nowrap">MGID {mgidSumme}</span>}
        {taboolaSumme && <span className="whitespace-nowrap">Taboola {taboolaSumme}</span>}
        {oaSumme && (
          <span className="whitespace-nowrap">
            OpenAI {oaSumme}
            {max && !schmal && <span className="cursor-help" data-max-kosten title={MAX_KOSTEN_ERKLAERUNG}> · {maxText}</span>}
          </span>
        )}
        {max && schmal && <span className="cursor-help whitespace-nowrap" data-max-kosten title={MAX_KOSTEN_ERKLAERUNG}>{maxText}</span>}
      </div>
    );
  };

  /** Werbekonten einer Marke als Fussleiste ihrer Karte: Kopfzeile + Kacheln in fester Breite (71 px). So sind alle
   *  Kacheln der Seite gleich gross und in den halben Karten (eToro, AvaTrade) passen sechs in eine Reihe. */
  const kontenFuss = (slug: string) => {
    const tiles = kontenDerMarke(slug);
    const p = PRODUCTS.find((x) => x.slug === slug);
    const laedt = (p?.google ? google === null : p?.meta ? meta === null : p?.reddit ? reddit === null : p?.mgid ? mgid === null : p?.taboola ? taboola === null : openai === null) || ((p?.microsoft || p?.msKonto || msTilesDer(slug).length > 0) && msCampaigns === null);
    return (
      <>
        {kopfzeile(slug, tiles, !HAUPT.includes(slug))}
        <div className="mt-2 grid gap-1.5" style={{ gridTemplateColumns: "repeat(auto-fill, 71px)", gridAutoRows: "1fr" }}>
          {laedt && tiles.length === 0
            ? [0, 1].map((i) => <Skeleton key={i} className="h-[107px]" />)
            : tiles.map((t) => (
              <AccountTile key={t.key} t={t} open={openTiles.has(t.key)} onToggle={() => toggleTile(t.key)} />
            ))}
          {!laedt && tiles.length === 0 && (
            <div className="col-span-full py-2 text-xs text-muted-foreground">Kein Werbekonto liefert Daten.</div>
          )}
        </div>
      </>
    );
  };

  /** Aufgeklappte Werbekonten einer Marke (Reihenfolge wie die Kacheln). */
  const offeneKonten = (slug: string) => kontenDerMarke(slug).filter((t) => openTiles.has(t.key));

  /** Zeilen der Partneruebersicht: Stufen nach Position (Reg / FTD / Trader), Kosten je Stufe aus den Ausgaben der Marke
   *  in USD, Werbekosten je Waehrung aus den Werbekonten. */
  const partnerZeilen = useMemo(() => {
    const pending = conversions === null;
    const alle = [...HAUPT, ...TEST_REIHEN.flat()].map((slug) => {
      const p = PRODUCTS.find((x) => x.slug === slug);
      const titel = markeTitel(slug);
      const tiles = kontenDerMarke(slug);
      const v = vergleichDaten[slug];
      const stufen: PartnerStufe[] = v
        ? (p?.stages ?? []).map((st, i) => {
            const zellen = v.zeilen.map((zl) => zl.cells[i]).filter((c): c is VergleichZelle => Boolean(c));
            return { key: st.key, label: st.label, count: pending ? null : zellen.reduce((x, c) => x + c.count, 0), rows: zellen.flatMap((c) => c.badges) };
          })
        : (markenCells[slug] ?? []).map((c) => ({
            key: c.key, label: c.titel, count: pending || (p?.nurEigeneKampagnen && msCampaigns === null) ? null : c.badges.reduce((x, r) => x + r.count, 0), rows: c.badges,
          }));
      const spend = spendUsd[slug] ?? 0;
      const jeWaehrung = new Map<string, number>();
      for (const t of tiles) jeWaehrung.set(t.currency, (jeWaehrung.get(t.currency) ?? 0) + t.spend);
      const werbekosten = jeWaehrung.size === 0 ? "—"
        : [...jeWaehrung.entries()].sort((a, b) => (a[0] === "USD" ? -1 : b[0] === "USD" ? 1 : 0))
            .filter(([, b], _i, arr) => b > 0 || arr.every(([, x]) => x === 0))
            .map(([cur, b]) => fmtGeld(b, cur)).join(" + ") || "—";
      const plattformen: Plattform[] = [
        ...(["microsoft", "chatgpt", "google", "meta", "reddit", "mgid", "taboola"] as const).filter((pl) => tiles.some((t) => t.platform === pl)),
        ...(p?.quellen?.includes("reddit") && !tiles.some((t) => t.platform === "reddit") ? (["reddit"] as const) : []),
      ];
      return {
        slug, titel, name: partnerName(titel), tiles, stufen, werbekosten, plattformen,
        klicks: tiles.reduce((x, t) => x + t.clicks, 0),
        kosten: (st: PartnerStufe | undefined) => (!st || !st.count || !spend ? "—" : fmtGeld(spend / st.count)),
      };
    });
    return alle;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversions, markenCells, vergleichDaten, spendUsd, openaiTiles, msTiles, sammelTiles, googleTile, googleKontoTiles, metaTile, redditTile, mgidTile, taboolaTile]);

  return (
    <div className="space-y-5">

      {/* Partneruebersicht (Operator 2026-10-09, nach Vorlage): alle Partner untereinander, je Zeile aufklappbar. Aufgeklappte
          Partner erscheinen als eigene Karte unter der Tabelle (Laender je Stufe + Werbekonten). */}
      <div className="mt-6 overflow-hidden rounded-xl border border-border bg-card" data-section="Partneruebersicht">
        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-3.5">
          <h2 className="text-[19px] font-bold tracking-tight">Partnerübersicht</h2>
          {/* Zeitraum fuer die ganze Seite (Operator 2026-10-09: statt Partnersuche, alte Auswahl darueber entfernt). */}
          <ZeitraumWaehler value={dateRange} onChange={setDateRange} />
        </div>
        <div className="overflow-x-auto">
          <div className="min-w-[860px]">
            <div className={cn(PARTNER_GRID, "h-10 whitespace-nowrap border-y border-border bg-muted/40 text-[12.5px] text-muted-foreground")}>
              <div>Partner</div><div>Traffic</div><div>Klicks</div><div>Reg.</div><div>FTD</div><div>Trader</div>
              <div>Kosten / Reg.</div><div>Kosten / FTD</div><div>Werbekosten</div><div />
            </div>
            {partnerZeilen.map((z) => {
              const offen = offenePartner.has(z.slug);
              const [reg, ftd, trader] = [z.stufen[0], z.stufen[1], z.stufen[2]];
              const zahl = (st: PartnerStufe | undefined) => !st ? "—" : st.count === null ? "…" : fmtInt(st.count);
              const herkunft = (r: ConversionRow) => fremdeKampagne(z.slug, r);
              return (
                <button
                  key={z.slug} type="button" onClick={() => togglePartner(z.slug)} aria-expanded={offen} data-partner={z.slug}
                  className={cn(PARTNER_GRID, "h-[46px] w-full border-b border-border text-left text-[13.5px] last:border-b-0 hover:bg-muted/30",
                    offen && "bg-[#eef3fd] hover:bg-[#e8effc] dark:bg-[#7aa2ff]/10 dark:hover:bg-[#7aa2ff]/15")}
                >
                  <div className="flex min-w-0 items-center gap-3.5">
                    <ProductIcon slug={z.slug} className="h-[30px] w-[30px] shrink-0" />
                    <span className="truncate font-semibold">{z.name}</span>
                  </div>
                  <div className="flex min-w-0 items-center gap-1.5 whitespace-nowrap">
                    {z.plattformen.map((pl, i) => (
                      <Fragment key={pl}>
                        {i > 0 && <span className="text-muted-foreground">·</span>}
                        <SourceIcon source={PLATTFORM_QUELLE[pl]} className="h-[19px] w-[19px] shrink-0" />
                        <span>{PLATTFORM_NAME[pl]}</span>
                      </Fragment>
                    ))}
                  </div>
                  <div className="tabular-nums">{z.tiles.length ? fmtInt(z.klicks) : "—"}</div>
                  <div className="tabular-nums" title={landGeraetTitel(reg, herkunft)}>{zahl(reg)}</div>
                  <div title={landGeraetTitel(ftd, herkunft)}>
                    {ftd && ftd.count
                      ? <span className="inline-flex h-6 min-w-[30px] items-center justify-center rounded-md bg-emerald-500/10 px-2 font-medium tabular-nums text-foreground">{fmtInt(ftd.count)}</span>
                      : <span className="tabular-nums">{zahl(ftd)}</span>}
                  </div>
                  <div className="tabular-nums" title={landGeraetTitel(trader, herkunft)}>{zahl(trader)}</div>
                  <div className="tabular-nums">{z.kosten(reg)}</div>
                  <div className="tabular-nums">{z.kosten(ftd)}</div>
                  <div className="whitespace-nowrap tabular-nums">{z.werbekosten}</div>
                  <div className="flex justify-end">
                    {offen ? <ChevronUp className={cn("h-[18px] w-[18px]", VORLAGE_BLAU)} /> : <ChevronDown className={cn("h-[18px] w-[18px] opacity-80", VORLAGE_BLAU)} />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {partnerZeilen.filter((z) => offenePartner.has(z.slug)).map((z) => {
        const mitZahlen = z.stufen.filter((st) => (st.count ?? 0) > 0);
        const offeneKampagnen = z.tiles.filter((t) => openTiles.has(t.key));
        const mitFtd = z.tiles.some((t) => t.ftd != null);
        // Herkunft der FTD-/Trader-Zahlen fuer den Tooltip: Microsoft = Zielattribution, Meta = Tracker (beide Klick-Datum).
        const ftdQuelle = [...new Set(z.tiles.filter((t) => t.ftd != null)
          .map((t) => (t.platform === "microsoft" ? "Microsoft-Attribution" : "Tracker")))].join(" / ");
        return (
          <section key={z.slug} className="rounded-xl border border-border bg-card px-6 py-4" data-partner-detail={z.slug}>
            <div className="flex items-center gap-3">
              <ProductIcon slug={z.slug} className="h-[30px] w-[30px] shrink-0" />
              <h3 className="truncate text-[19px] font-bold tracking-tight">{z.titel}</h3>
              <button type="button" onClick={() => togglePartner(z.slug)} className="ml-auto rounded p-1 hover:bg-muted" aria-label="Zuklappen">
                <ChevronUp className={cn("h-[18px] w-[18px]", VORLAGE_BLAU)} />
              </button>
            </div>
            <div className="mt-3.5 grid gap-6 md:grid-cols-[minmax(0,0.72fr)_minmax(0,1fr)]">
              <div className="min-w-0 md:border-r md:border-border md:pr-6">
                <div className="text-[13.5px] font-semibold">Länder</div>
                <div className="mt-2 space-y-2.5">
                  {mitZahlen.map((st) => (
                    <div key={st.key} className="rounded-xl border border-border px-3.5 py-3" data-partner-stufe={`${z.slug}:${st.key}`}>
                      {/* Stufe + Geraete (Balken, Symbol und Zahl) in einer Zeile (Operator 2026-10-09). */}
                      <div className="flex items-center gap-2">
                        <div className="shrink-0 whitespace-nowrap text-[13.5px] font-semibold">{STUFE_LANG[st.key] ?? st.label} · {fmtInt(st.count ?? 0)}</div>
                        <QuellenChips rows={st.rows} />
                        <DeviceSplit rows={st.rows} />
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {/* Conversion aus einer Kampagne ausserhalb der Karte: Konto + Kampagne dazuschreiben (Operator 2026-10-29). */}
                        {landGeraetVon(st.rows, (r) => fremdeKampagne(z.slug, r)).map((l) => (
                          <span key={`${l.geo}|${l.device}|${l.herkunft ?? ""}`}
                            className="inline-flex h-7 min-w-0 max-w-full items-center rounded-md border border-border bg-muted/40 px-2.5 text-[12.5px] tabular-nums"
                            data-land-geraet={`${l.geo}|${l.device}`} data-herkunft={l.herkunft ?? undefined}
                            title={l.herkunft ? `${landGeraetText(l)} ${fmtInt(l.count)} aus ${l.herkunft}` : undefined}>
                            <span className="whitespace-nowrap">{landGeraetText(l)} {fmtInt(l.count)}</span>
                            {l.herkunft && <span className="ml-1 truncate text-muted-foreground">· aus {l.herkunft}</span>}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                  {mitZahlen.length === 0 && <div className="text-[13px] text-muted-foreground">Noch keine Conversions im Zeitraum.</div>}
                </div>
                {z.slug === "1xbet" && <OnexbetKasten daten={onexbet} fehler={onexbetError} />}
              </div>
              <div className="min-w-0">
                <div className="text-[13.5px] font-semibold">Kampagnen</div>
                <div className="mt-2 overflow-hidden rounded-lg border border-border">
                  <div className={cn(mitFtd ? KAMPAGNEN_GRID_FTD : KAMPAGNEN_GRID, "h-8 bg-muted/40 text-[12.5px] text-muted-foreground")}>
                    <div>Kampagne</div><div>Impr.</div><div>Klicks</div><div>Kosten</div><div>{mitFtd ? "Conv." : "Conversions"}</div>
                    {mitFtd && <><div title={`Ersteinzahlungen (${ftdQuelle}, Klick-Datum)`}>FTD</div><div title={`Trader = eToro-CPA (${ftdQuelle}, Klick-Datum)`}>Trader</div></>}
                  </div>
                  {z.tiles.map((t) => (
                    <button key={t.key} type="button" onClick={() => toggleTile(t.key)} aria-expanded={openTiles.has(t.key)}
                      className={cn(mitFtd ? KAMPAGNEN_GRID_FTD : KAMPAGNEN_GRID, "h-8 w-full border-t border-border text-left text-[13px] hover:bg-muted/30",
                        openTiles.has(t.key) && "bg-[#eef3fd] dark:bg-[#7aa2ff]/10")}>
                      <div className="flex min-w-0 items-center gap-3">
                        <SourceIcon source={PLATTFORM_QUELLE[t.platform]} className="h-4 w-4 shrink-0" />
                        <span className="truncate">{kampagnenName(t)}</span>
                      </div>
                      <div className="tabular-nums">{fmtInt(t.impressions ?? 0)}</div>
                      <div className="tabular-nums">{fmtInt(t.clicks)}</div>
                      <div className="tabular-nums">{fmtGeld(t.spend, t.currency)}</div>
                      <div className="tabular-nums">{fmtInt(t.conversions)}</div>
                      {mitFtd && <><FtdZelle n={t.ftd} /><FtdZelle n={t.trader} /></>}
                    </button>
                  )).flatMap((zeile, i) => {
                    const t = z.tiles[i]!;
                    if (!t.unterzeilen?.length) return [zeile];
                    const zahlen = (u: Unterzeile) => (
                      <>
                        <div className="tabular-nums">{fmtInt(u.impressions)}</div>
                        <div className="tabular-nums">{fmtInt(u.clicks)}</div>
                        <div className="tabular-nums">{fmtGeld(u.spend, t.currency)}</div>
                        <div className="tabular-nums">{fmtInt(u.conversions)}</div>
                        {mitFtd && <><FtdZelle n={u.ftd} /><FtdZelle n={u.trader} /></>}
                      </>
                    );
                    return [zeile, ...t.unterzeilen.flatMap((u) => {
                      if (!u.aufKey || !u.gruppen) {
                        return [
                          <div key={`${t.key}:${u.key}`} data-adgroup={u.name}
                            className={cn(mitFtd ? KAMPAGNEN_GRID_FTD : KAMPAGNEN_GRID, "h-7 border-t border-border/60 text-[12.5px] text-muted-foreground")}>
                            <div className="truncate pl-7" title={u.name}>{u.name}</div>
                            {zahlen(u)}
                          </div>,
                        ];
                      }
                      // Google-Kampagne (Operator 2026-10-23): Klick zeigt ihre Anzeigengruppen direkt darunter.
                      const auf = openTiles.has(u.aufKey);
                      return [
                        <button key={`${t.key}:${u.key}`} type="button" onClick={() => toggleTile(u.aufKey!)} aria-expanded={auf}
                          data-gcampaign={u.key} title={`${u.name} – Klick zeigt die Anzeigengruppen`}
                          className={cn(mitFtd ? KAMPAGNEN_GRID_FTD : KAMPAGNEN_GRID, "h-7 w-full border-t border-border/60 text-left text-[12.5px] hover:bg-muted/30",
                            auf ? "bg-[#eef3fd] text-foreground dark:bg-[#7aa2ff]/10" : "text-muted-foreground")}>
                          <div className="flex min-w-0 items-center gap-1 pl-5">
                            <ChevronDown className={cn("h-3.5 w-3.5 shrink-0 transition-transform", auf && "rotate-180")} />
                            <span className="truncate">{u.name}</span>
                          </div>
                          {zahlen(u)}
                        </button>,
                        ...(auf ? u.gruppen.map((g) => (
                          <div key={`${t.key}:${u.key}:${g.key}`} data-adgroup={g.name}
                            className={cn(mitFtd ? KAMPAGNEN_GRID_FTD : KAMPAGNEN_GRID, "h-7 border-t border-border/40 text-[12px] text-muted-foreground")}>
                            <div className="truncate pl-11" title={g.name}>{g.name}</div>
                            {zahlen(g)}
                          </div>
                        )) : []),
                      ];
                    })];
                  })}
                  {z.tiles.length === 0 && <div className="border-t border-border px-3.5 py-2 text-[13px] text-muted-foreground">Kein Werbekonto liefert Daten.</div>}
                </div>
                {/* Aufgeklappte Werbekonten in der rechten Spalte direkt unter der Kampagnentabelle (Operator 2026-10-10). */}
                {offeneKampagnen.length > 0 && (
                  <div className="mt-3 grid grid-cols-1 gap-3" data-section={`Kampagnen ${z.slug}`}>
                    {offeneKampagnen.map((t) => <DetailPanel key={t.key} t={t} onClose={() => toggleTile(t.key)} />)}
                  </div>
                )}
              </div>
            </div>
          </section>
        );
      })}

      {/* Kapital unter den Marken-Kacheln (Operator 2026-10-02): jetzt, nach der naechsten und nach der uebernaechsten
          Provisionszahlung; aufklappbar der Kontoverlauf bei gleichbleibenden Ausgaben. */}
      {/* Kapital auf halber Breite (Operator 2026-10-09), rechts daneben die Kachel "Seite" mit den Landing-Kennzahlen je
          Kampagne (2026-10-10); unter lg untereinander. Beide klappen unabhaengig auf (items-start). */}
      <div className="space-y-5 lg:flex lg:items-start lg:space-y-0">
        <div className="min-w-0 lg:w-1/2"><KapitalKarte /></div>
        <div className="min-w-0 lg:w-1/2 lg:pl-5"><SeiteKarte dateRange={dateRange} /></div>
      </div>

      {conversionsError && <div className="text-xs text-destructive">Tracker: {conversionsError}</div>}
      {chatgptErrors.length > 0 && <div className="text-xs text-destructive">OpenAI Ads: {chatgptErrors.join(" · ")}</div>}
      {msError && <div className="text-xs text-destructive">MS-Ads: {msError}</div>}
    </div>
  );
}
